import {
  areStringsEqualIgnoreCase,
  BusinessEntity,
  Client,
  compareStringsIgnoreCase,
  DEFAULT_LOADABLE_STATE,
  extractFailureContext,
  forceArray,
  getLoadedResults,
  hashCode,
  ignoreEmpty,
  ignoreFalsy,
  ignoreTruthy,
  isEmpty,
  Loadable,
  LoadableError,
  LoadableHelper,
  LoadableStatus,
  Logger,
  LogIn,
  NonEmptyArray,
  normalizeCountryAlpha2Code,
  removeDuplicates,
  removeDuplicatesByField,
  removeEntityByEntry,
  replaceArrayItem,
  ResetState,
  round,
  roundMonetaryValue,
  setLoadableInState,
  UsState,
  UsStateAbbreviation,
} from '@aiwyn/shared';
import {
  AIWYN_VALIDATION_ERROR_CATEGORY,
  CreditApplicationTrackService,
  CreditApplicationWorkflowEvent,
  PaymentEventBaseMetadata,
  PaymentMethodSurface,
  PaymentSurface,
  PaymentTrackService,
  PaymentType,
  PaymentWorkflowEvent,
} from '../tracking';
import { inject, Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { v4 as uuidv4 } from 'uuid';
import { ROUTE_TOKEN_ENGAGEMENTS_BASE, ROUTE_TOKEN_QUICK_PAYMENTS, ROUTE_TOKEN_REVIEW_AND_PAY } from '../../../routing-constants';
import {
  AdditionalServiceApiService,
  ApplyCreditsPayload,
  ArTranAllocation,
  ArTranAllocationWithClientId,
  AutoPay,
  BaseAdminPaymentDetails,
  BaseClient,
  BaseJob,
  BaseLateFee,
  BaseUserPaymentDetails,
  CardFunding,
  ChargeDetails,
  ChargePayload,
  ClientApiService,
  Credit,
  CreditAllocation,
  CreditApiService,
  CreditLabel,
  CreditFetchResult,
  Currency,
  CurrencyCode,
  DEFAULT_CURRENCY,
  FeeApiService,
  InvoiceApiService,
  InvoiceChargePayload,
  JobApiService,
  LateFeeFetchResult,
  OpenPaymentLinkResult,
  PayableUi,
  PaymentActionRequirement,
  PaymentFeeConfig,
  PaymentFeePaymentMethodType,
  PaymentFeeType,
  PaymentMethodType,
  PaymentPlatform,
  PaymentsApiService,
  WithoutInvoiceChargePayload,
} from '@aiwyn/api';
import { combineLatest, concat, defer, EMPTY, merge, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, mapTo, skip, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AuthAdaptorService, AuthService, AuthStateAdaptorService } from '@aiwyn/auth';
import {
  AddAddOn,
  AddCreditAppliedClientIds,
  AddPayLateFeeClientIds,
  ApplyPaymentToArTranAllocations,
  CancelCurrentPayment,
  ClearAddOns,
  ClearArTranAllocations,
  ClearCreditAppliedClientIds,
  ClearCurrentPaymentActionRequirement,
  ClearLateFees,
  ClearNewPaymentMethodData,
  ClearPayLateFeeClientIds,
  ClearQuickPaymentLinkId,
  ClearSuccessfulPaymentData,
  FillPartialArTranAllocations,
  GetAddOnOptions,
  GetClients,
  GetCreditLabels,
  GetCredits,
  GetJobs,
  GetLateFees,
  GetPayables,
  GetPaymentFeeConfigs,
  GetQuickPaymentLinkId,
  MakePayment,
  RemoveAddOn,
  RemoveArTranAllocations,
  RemoveCreditAppliedClientIds,
  RemoveInapplicableCreditAppliedClientIds,
  RemoveInapplicablePayLateFeeClientIds,
  RemovePayLateFeeClientIds,
  RemovePayWithoutInvoicePayable,
  SetActivePlatform,
  SetAdditionalRecipientEmail,
  SetAddOns,
  SetArTranAllocations,
  SetCurrentPaymentActionRequirement,
  SetCustomProcessingFeeRate,
  SetDefaultActivePlatform,
  SetPaymentEntrySource,
  SetPayLateFees,
  SetPayWithInvoices,
  SetProvidedPrimaryRecipientEmail,
  SetShouldSaveNewPaymentMethod,
  SetShouldSetNewPaymentMethodAsDefault,
  SubmitCreditAllocations,
  UpdateArTranAllocations,
  UpdatePayWithoutInvoicePayable,
} from './payments.actions';
import { DEFAULT_PAYMENTS_STATE_MODEL, PaymentsStateModel } from './payments.model';
import { AbstractPaymentMethod, PaymentPlatformPaymentData, PaymentPlatformService, StripeState } from '../platforms';
import { ConfigModel, ConfigService, ConfigState, Globals, LaunchDarklyFlag, LaunchDarklyService, LaunchDarklyState } from '@aiwyn/config';
import { PayableTableObject, PayableType } from '../../../components/main/invoices/models/payable-table-object';
import { CommonSettingsState } from '@aiwyn/common/settings';
import { AddOn, AddOnOption } from '../../../model';
import {
  ChargePaymentError,
  ClientPaymentSummary,
  ExecutedPaymentBatch,
  ExecutedPaymentBatches,
  PayOpenPortalPayable,
  PayWithoutInvoicePayable,
} from '../models';
import { ApolloError } from '@apollo/client/errors';
import { sumBy } from 'lodash';

type SharedBaseChargePayload = Pick<ChargePayload, 'contactId' | 'businessEntityId'>;
type SharedBaseChargeDetails = Pick<ChargeDetails, 'setupIntentId' | 'paymentMethodId' | 'notifyToList'>;
type SharedBasePaymentDetailsFields =
  'applicationFeeFixedAmount'
  | 'applicationFeeCapAmount'
  | 'businessEntityId'
  | 'lateFeeIds'
  | 'clientIdList';
type SharedBaseUserPaymentDetailsFields = SharedBasePaymentDetailsFields | 'contactId';
type SharedBaseAdminPaymentDetailsFields = SharedBasePaymentDetailsFields | 'processingFeeOverridden' | 'staffId';
type SharedBaseUserPaymentDetails = Pick<BaseUserPaymentDetails, SharedBaseUserPaymentDetailsFields>;
type SharedBaseAdminPaymentDetails = Pick<BaseAdminPaymentDetails, SharedBaseAdminPaymentDetailsFields>;
type SharedBasePaymentDetails = SharedBaseUserPaymentDetails | SharedBaseAdminPaymentDetails;
type SharedBaseChargePayloadParts = {
  chargePayload: SharedBaseChargePayload;
  chargeDetails: SharedBaseChargeDetails;
  paymentDetails: SharedBasePaymentDetails;
  paymentLinkId?: number;
};

export interface GetTotalOptions {
  subtotal?: number;
  isPaymentPlan?: boolean;
  ignoreLateFees?: boolean;
  currencyCode?: CurrencyCode;
  businessEntityId?: number;
}

const IDEMPOTENCY_KEY_TIME_WINDOW = 300000 as const; // 5 minutes in ms
const IDEMPOTENCY_KEY_DELIMITER = '█' as const; // ASCII 219
const IDEMPOTENCY_KEY_MAX_LENGTH = 255 as const; // 255 characters

export const INVALID_AR_TRAN_ALLOCATION_ERROR =
  'AR transaction allocation amount must be greater than zero and no greater than the outstanding balance';

export const PAYMENTS_STATE_SLICE_NAME = 'payments';

@Injectable({
  providedIn: 'root',
})
@State<PaymentsStateModel>({
  name: PAYMENTS_STATE_SLICE_NAME,
  defaults: DEFAULT_PAYMENTS_STATE_MODEL,
  children: [StripeState],
})
export class PaymentsState {
  private readonly paymentTrackService = inject(PaymentTrackService);
  private readonly creditApplicationTrackService = inject(CreditApplicationTrackService);

  @Selector()
  static getActivePlatformLoadable({ activePlatform }: PaymentsStateModel): Loadable<PaymentPlatform> {
    return activePlatform;
  }

  @Selector([PaymentsState.getActivePlatformLoadable])
  static getActivePlatformStatus(activePlatformLoadable: Loadable<PaymentPlatform>): LoadableStatus {
    return activePlatformLoadable?.status;
  }

  @Selector([PaymentsState.getActivePlatformLoadable])
  static getActivePlatform(activePlatformLoadable: Loadable<PaymentPlatform>): PaymentPlatform {
    return activePlatformLoadable?.data;
  }

  @Selector()
  static getRawPayablesLoadable({ payables }: PaymentsStateModel): Loadable<PayableUi[]> {
    return payables;
  }

  @Selector([PaymentsState.getRawPayablesLoadable])
  static getRawPayablesStatus({ status }: Loadable<PayableUi[]>): LoadableStatus {
    return status ?? LoadableStatus.DEFAULT;
  }

  @Selector([PaymentsState.getRawPayablesLoadable])
  static getRawPayables({ data }: Loadable<PayableUi[]>): PayableUi[] {
    return data;
  }

  @Selector([
    PaymentsState.getRawPayablesLoadable,
    CommonSettingsState.getAutoPayClientsLoadable,
    ConfigState.getBusinessEntitiesLoadable,
    ConfigState.isDueUponReceipt,
  ])
  static getPayablesLoadable(
    payables: Loadable<PayableUi[]>,
    autoPayClients: Loadable<AutoPay[]>,
    businessEntities: Loadable<BusinessEntity[]>,
    dueUponReceipt: boolean,
  ): Loadable<PayableTableObject[]> {
    const coalescedStatuses = LoadableHelper.coalesceStatus([payables?.status, autoPayClients?.status, businessEntities?.status]);
    if (coalescedStatuses !== LoadableStatus.LOADED) {
      return { status: coalescedStatuses };
    }

    return {
      status: LoadableStatus.LOADED,
      data: (payables?.data ?? []).map(payable => {
        const businessEntity = businessEntities?.data.find(({ id }) => id === payable.payable.arTran.businessEntityId)
          ?? businessEntities?.data.find(({ isDefault }) => isDefault);

        return PayableTableObject.create(payable, autoPayClients.data, businessEntity, dueUponReceipt);
      }),
    };
  }

  @Selector([PaymentsState.getPayablesLoadable])
  static getPayablesStatus({ status }: Loadable<PayableTableObject[]>): LoadableStatus {
    return status ?? LoadableStatus.DEFAULT;
  }

  @Selector([PaymentsState.getPayablesLoadable])
  static getPayables(payablesLoadable: Loadable<PayableTableObject[]>): PayableTableObject[] {
    return payablesLoadable?.data;
  }

  static getPayable(arTranId: number): (payables: PayableTableObject[]) => PayableTableObject {
    return createSelector(
      [PaymentsState.getPayables],
      payables => (payables ?? []).find(payable => payable.arTranId === arTranId),
    );
  }

  @Selector([PaymentsState.getArTranAllocations])
  static getSelectedPayablesSubtotal(arTranAllocations: ArTranAllocation[]): number {
    if (arTranAllocations == undefined) {
      return undefined;
    }

    const rawSubtotal = arTranAllocations.reduce((acc, { allocatedAmount }) => acc + allocatedAmount, 0);
    return roundMonetaryValue(rawSubtotal);
  }

  static getSelectedClientPayables(
    clientId: number,
  ): (payables: PayableTableObject[], arTranAllocations: ArTranAllocation[]) => PayableTableObject[] {
    return createSelector(
      [PaymentsState.getPayables, PaymentsState.getArTranAllocations],
      (payables, arTranAllocations) => {
        const selectedArTranIds = arTranAllocations.map(({ arTranId }) => arTranId);
        return payables.filter(({ invoice }) => invoice.arTran.clientId === clientId && selectedArTranIds.includes(invoice.arTran.id));
      },
    );
  }

  @Selector([PaymentsState.getPayables])
  static areMultipleBusinessEntitiesPresent(payables: PayableTableObject[]): boolean {
    return payables && removeDuplicatesByField(payables, 'entityId').length > 1;
  }

  @Selector([PaymentsState.getPayables])
  static getBusinessEntityIdWithMostAmountDue(payables: PayableTableObject[]): number {
    // Convert this to a dictionary of EntityId, and amountDue
    const businessEntityAmountDueMap = payables.reduce(
      (acc, payable) => ({ ...acc, [payable.entityId]: (acc[payable.entityId] ?? 0) + payable.amountRemaining }),
      {} as Record<string, number>,
    );

    const [[entityIdWithMaxAmountRemaining]] = Object.entries(businessEntityAmountDueMap).sort(([, a], [, b]) => b - a);
    return +entityIdWithMaxAmountRemaining;
  }

  @Selector()
  static getPayWithInvoices({ payWithInvoices }: PaymentsStateModel): boolean {
    return payWithInvoices;
  }

  @Selector()
  static getSelectedPayableIds({ arTranAllocations }: PaymentsStateModel): number[] {
    return (arTranAllocations ?? []).map(({ arTranId }) => arTranId);
  }

  @Selector([PaymentsState.getSelectedPayableIds, PaymentsState.getPayables])
  static getSelectedPayables(selectedPayableArTranIds: number[], payables: PayableTableObject[]): PayableTableObject[] {
    return (payables ?? []).filter(({ arTranId }) => selectedPayableArTranIds.includes(arTranId));
  }

  @Selector()
  static getCreditLabelsLoadable({ creditLabels }: PaymentsStateModel): Loadable<CreditLabel[]> {
    return creditLabels;
  }

  @Selector([PaymentsState.getCreditLabelsLoadable])
  static getCreditLabelsStatus(creditLabelsLoadable: Loadable<CreditLabel[]>): LoadableStatus {
    return creditLabelsLoadable?.status;
  }

  @Selector([PaymentsState.getCreditLabelsLoadable])
  static getCreditLabels(creditLabelsLoadable: Loadable<CreditLabel[]>): CreditLabel[] {
    return creditLabelsLoadable?.data;
  }

  @Selector()
  static getCreditsLoadable({ credits }: PaymentsStateModel): Loadable<Credit[]> {
    return credits;
  }

  @Selector([PaymentsState.getCreditsLoadable])
  static getCreditsStatus(creditsLoadable: Loadable<Credit[]>): LoadableStatus {
    return creditsLoadable?.status;
  }

  @Selector([PaymentsState.getCreditsLoadable])
  static getCredits(creditsLoadable: Loadable<Credit[]>): Credit[] {
    return creditsLoadable?.data;
  }

  static getCredit(creditId: number): (credits: Credit[]) => Credit {
    return createSelector(
      [PaymentsState.getCredits],
      credits => (credits ?? []).find(({ id }) => id === creditId),
    );
  }

  @Selector()
  static getCreditAppliedClientIds({ creditAppliedClientIds }: PaymentsStateModel): number[] {
    return creditAppliedClientIds;
  }

  @Selector([
    PaymentsState.getCreditAppliedClientIds,
    PaymentsState.getCredits,
    PaymentsState.getArTranAllocationsWithClientIds,
    LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_APPLY_CREDITS_TO_LATE_FEES),
    PaymentsState.getSelectedLateFees,
  ])
  static getCreditAllocations(
    creditAppliedClientIds: number[],
    credits: Credit[],
    arTranAllocations: ArTranAllocationWithClientId[],
    payLateFeesByClientEnabled: boolean,
    selectedLateFees: BaseLateFee[],
  ): CreditAllocation[] {
    if (isEmpty(creditAppliedClientIds) || isEmpty(arTranAllocations)) {
      return [];
    }

    const arTranAllocationsByClientId: Record<number, ArTranAllocation[]> = arTranAllocations.reduce(
      (acc, { clientId, ...allocation }) => ({ ...acc, [clientId]: (acc[clientId] ?? []).concat(allocation) }),
      {},
    );
    const creditsByClientId: Record<number, Credit[]> = credits.reduce(
      (acc, { clientId, ...credit }) => ({ ...acc, [clientId]: (acc[clientId] ?? []).concat(credit) }),
      {},
    );
    const lateFeeAllocationsByClientId: Record<number, ArTranAllocation[]> = payLateFeesByClientEnabled
      ? selectedLateFees.reduce(
        (acc, { clientId, ...lateFee }) => ({
          ...acc,
          [clientId]: (acc[clientId] ?? []).concat({ arTranId: lateFee.id, allocatedAmount: lateFee.amount, active: lateFee.isActive }),
        }),
        {},
      )
      : {};

    return creditAppliedClientIds.reduce(
      (creditAllocations, creditAppliedClientId) => {
        // All the Pokémon each side has ready for battle
        const arTranAllocationsQueue = (lateFeeAllocationsByClientId[creditAppliedClientId] ?? []) // prioritize late fees
          .concat(arTranAllocationsByClientId[creditAppliedClientId] ?? []);
        const creditsQueue = creditsByClientId[creditAppliedClientId] ?? [];

        // Pokémon you choose first
        let activeArTranAllocation: ArTranAllocation = { ...arTranAllocationsQueue.shift() };
        let activeCredit: Credit = { ...creditsQueue.shift() };

        // Log of all the damage dealt
        let clientCreditAllocations: CreditAllocation[] = [];

        /**
         * Imagine you're playing Pokémon. Your opponent has payables, and you have credits. Each round, only your credits will attack.
         * Payables are playing an entirely defensive game, but they have a special ability where all the damage your credits deal is
         * fully reflected back onto the credits as well. Credits can deal damage up to the amount of health they have left. If the
         * payable's remaining health is less than the credit's, then the payable will faint, and the credit will be dealt damage equal to
         * the payable's remaining health. The match is over when all Pokémon on one side faint. That means all allocations have been made.
         * The match is over, and your wallet is now empty. Darn you, Meowth!
         *
         * Examples:
         * - Credit 1: 500
         * - Payable 1: 300
         * - Result: Credit: 200, Payable: 0, Allocation: 300
         * - Next turn, Credit 1 will deal 200 damage to Payable 2
         *
         * - Credit 1: 400
         * - Payable 1: 500
         * - Result: Credit: 0, Payable: 100, Allocation: 400
         * - Next turn, Credit 2 will deal 100 damage to Payable 1
         */
        do {
          // Choose new Pokémon if the current one has fainted (has to either be one or both)
          if (activeArTranAllocation.allocatedAmount === 0) {
            activeArTranAllocation = { ...arTranAllocationsQueue.shift() };
          }
          if (activeCredit.amountRemaining === 0) {
            activeCredit = { ...creditsQueue.shift() };
          }

          // Find which Pokémon has the least amount of health remaining, and drain that amount from each Pokémon
          const creditAllocationAmount = Math.max(-activeArTranAllocation.allocatedAmount, activeCredit.amountRemaining);
          activeArTranAllocation.allocatedAmount += creditAllocationAmount;
          activeCredit.amountRemaining -= creditAllocationAmount;

          // Record the damage dealt
          clientCreditAllocations = clientCreditAllocations.concat({
            modARTranId: activeCredit.id,
            allocated: roundMonetaryValue(creditAllocationAmount),
            arTranId: activeArTranAllocation.arTranId,
          });
        } while (
          // The match is over when all Pokémon on one side faint.
        !(isEmpty(arTranAllocationsQueue) && activeArTranAllocation.allocatedAmount === 0)
        && !(isEmpty(creditsQueue) && activeCredit.amountRemaining === 0)
          );

        return creditAllocations.concat(clientCreditAllocations);
      },
      new Array<CreditAllocation>(),
    );
  }

  @Selector([
    PaymentsState.getCreditAppliedClientIds,
    PaymentsState.getCredits,
    PaymentsState.getArTranAllocationsWithClientIds,
    PaymentsState.getSelectedLateFees,
    LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_APPLY_CREDITS_TO_LATE_FEES),
  ])
  static getClientAppliedCreditTotals(
    creditAppliedClientIds: number[],
    credits: Credit[],
    arTranAllocations: ArTranAllocationWithClientId[],
    selectedLateFees: BaseLateFee[],
    payLateFeesByClientEnabled: boolean,
  ): Record<string, number> {
    if (isEmpty(creditAppliedClientIds) || isEmpty(arTranAllocations)) {
      return {};
    }

    return creditAppliedClientIds.reduce(
      (acc, creditAppliedClientId) => {
        const lateFeeAllocations: ArTranAllocationWithClientId[] = (payLateFeesByClientEnabled ? selectedLateFees : [])
          .filter(({ clientId }) => clientId === creditAppliedClientId)
          .map(({ id, amount, isActive }) => ({
            arTranId: id,
            allocatedAmount: amount,
            active: isActive,
            clientId: creditAppliedClientId,
          }));

        const clientArTranAllocations = lateFeeAllocations.concat(arTranAllocations)
          .filter(({ clientId }) => clientId === creditAppliedClientId);
        const arTranAllocationsTotal = clientArTranAllocations.reduce((acc, { allocatedAmount }) => acc + allocatedAmount, 0);

        const clientCredits = credits.filter(({ clientId }) => clientId === creditAppliedClientId);
        const creditsTotal = clientCredits.reduce((acc, { amountRemaining }) => acc + amountRemaining, 0);

        const clientTotal = Math.max(-arTranAllocationsTotal, creditsTotal);
        return { ...acc, [creditAppliedClientId]: roundMonetaryValue(clientTotal) };
      },
      {} as Record<string, number>,
    );
  }

  @Selector([PaymentsState.getClientAppliedCreditTotals])
  static getAppliedCreditTotal(clientAppliedCreditTotals: Record<string, number>): number {
    const total = Object.values(clientAppliedCreditTotals).reduce((acc, total) => acc + total, 0);
    return roundMonetaryValue(total);
  }

  @Selector([
    LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_APPLY_CREDITS_TO_LATE_FEES),
    PaymentsState.getArTranAllocationsTotal,
    PaymentsState.getSelectedLateFeesTotal,
    PaymentsState.getAppliedCreditTotal,
  ])
  static doAppliedCreditsCoverArTranAllocations(
    isPayLateFeesByClientEnabled: boolean,
    arTranAllocationsTotal: number,
    selectedLateFeesTotal: number,
    appliedCreditTotal: number,
  ): boolean {
    if (!appliedCreditTotal) {
      return false;
    }

    const subtotalAfterApplyingCredits = roundMonetaryValue(
      arTranAllocationsTotal + (isPayLateFeesByClientEnabled ? selectedLateFeesTotal : 0) + appliedCreditTotal,
    );
    return subtotalAfterApplyingCredits === 0;
  }

  // Late fees are ignored here.
  @Selector([PaymentsState.doAppliedCreditsCoverArTranAllocations, PaymentsState.getAddOnsSubtotal])
  static doAppliedCreditsCoverSubtotal(doCreditsCoverArTranAllocations: boolean, addOnsSubtotal: number): boolean {
    return doCreditsCoverArTranAllocations && addOnsSubtotal === 0;
  }

  @Selector([
    PaymentsState.getPayables,
    PaymentsState.getArTranAllocations,
    PaymentsState.getAddOns,
    LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_APPLY_CREDITS_TO_LATE_FEES),
    PaymentsState.getCanPayLateFees,
    PaymentsState.getLateFees,
    PaymentsState.getPayLateFeeClientIds,
    PaymentsState.getCredits,
    PaymentsState.getClientAppliedCreditTotals,
  ])
  static getClientPaymentSummaries(
    payables: PayableTableObject[],
    arTranAllocations: ArTranAllocation[],
    addOns: AddOn[],
    canPayLateFeesByClient: boolean,
    canPayLateFees: boolean,
    lateFees: BaseLateFee[],
    payLateFeeClientIds: number[],
    credits: Credit[],
    clientAppliedCreditTotals: Record<string, number>,
  ): ClientPaymentSummary[] {
    if (isEmpty(arTranAllocations) && isEmpty(addOns) || payables == undefined) {
      return [];
    }

    const clientIdsFromPayables: BaseClient[] = arTranAllocations.map(arTranAllocation => {
      const matchingPayable = payables.find(({ arTranId }) => arTranId === arTranAllocation.arTranId);
      return ({
        id: matchingPayable.invoice.arTran.clientId,
        name: matchingPayable.clientName,
      });
    });
    const clientIdsFromAddOns: BaseClient[] = addOns.map(addOn => ({ id: addOn.account.id, name: addOn.account.name }));
    const clientIdsFromLateFees: BaseClient[] = (canPayLateFees ? payLateFeeClientIds : []).map(clientId => {
      const matchingPayable = payables.find(({ invoice }) => invoice.arTran.clientId === clientId);
      return ({
        id: clientId,
        name: matchingPayable.clientName,
      });
    });
    const uniqueBaseClients = removeDuplicatesByField(
      clientIdsFromPayables.concat(clientIdsFromAddOns).concat(clientIdsFromLateFees),
      'id',
    );
    const sortedBaseClients = uniqueBaseClients.sort((a, b) => compareStringsIgnoreCase(a.name, b.name));

    const clientPaymentSummaries: ClientPaymentSummary[] = sortedBaseClients.map(client => {
      /** payables **/
      const clientPayables = arTranAllocations.filter(arTranAllocation => {
        const matchingPayable = payables.find(({ arTranId }) => arTranId === arTranAllocation.arTranId);
        return matchingPayable.invoice.arTran.clientId === client.id;
      });
      const payablesTotal = clientPayables.reduce((acc, { allocatedAmount }) => acc + allocatedAmount, 0);
      const payablesSummary = { count: clientPayables.length, total: payablesTotal };

      /** prepayments (PP-3636): the prepayment subset of the client's selected payables, shown as its own line. */
      const clientPrepayments = clientPayables.filter(arTranAllocation =>
        payables.find(({ arTranId }) => arTranId === arTranAllocation.arTranId)?.getType() === PayableType.PREPAYMENT,
      );
      const prepaymentsSummary = {
        count: clientPrepayments.length,
        total: clientPrepayments.reduce((acc, { allocatedAmount }) => acc + allocatedAmount, 0),
      };

      /** late fees **/
      const clientLateFees = canPayLateFeesByClient ? lateFees.filter(lateFee => lateFee.clientId === client.id) : [];
      const lateFeesTotal = roundMonetaryValue(sumBy(clientLateFees, 'amount'));
      const lateFeesApplied = payLateFeeClientIds.includes(client.id) ? lateFeesTotal : 0;

      /** credits **/
      const clientCredits = (credits ?? []).filter(({ clientId }) => clientId === client.id);
      const creditTotal = roundMonetaryValue(sumBy(clientCredits, 'amountRemaining'));
      const creditApplied = clientAppliedCreditTotals[client.id] ?? 0;

      /** add-ons **/
      const clientAddOns = addOns.filter(addOn => addOn.account.id === client.id);
      const addOnsTotal = roundMonetaryValue(clientAddOns.reduce((acc, { paymentAmount }) => acc + +paymentAmount, 0));

      const subtotal = payablesTotal + lateFeesApplied + creditApplied + addOnsTotal;
      return {
        client,
        payables: payablesSummary,
        prepayments: prepaymentsSummary,
        addOns: clientAddOns,
        lateFeesTotal,
        lateFeesApplied,
        creditTotal,
        creditApplied,
        subtotal,
      };
    });

    return clientPaymentSummaries;
  }

  @Selector()
  static getAddOnOptionsLoadable({ addOnOptions }: PaymentsStateModel): Loadable<AddOnOption[]> {
    return addOnOptions;
  }

  @Selector([PaymentsState.getAddOnOptionsLoadable])
  static getAddOnOptionsStatus({ status }: Loadable<AddOnOption[]>): LoadableStatus {
    return status;
  }

  @Selector([PaymentsState.getAddOnOptionsLoadable])
  static getAddOnOptions({ data }: Loadable<AddOnOption[]>): AddOnOption[] {
    return data;
  }

  static getAddOnOption(addOnTypeId: number): (addOnOptions: AddOnOption[]) => AddOnOption {
    return createSelector(
      [PaymentsState.getAddOnOptions],
      addOnOptions => addOnOptions.find(({ id }) => id === addOnTypeId),
    );
  }

  @Selector()
  static getAddOns({ addOns }: PaymentsStateModel): AddOn[] {
    return addOns ?? [];
  }

  @Selector([PaymentsState.getAddOns])
  static getAddOnsSubtotal(addOns: AddOn[]): number {
    return addOns.reduce((subtotal, { paymentAmount }) => subtotal + +paymentAmount, 0);
  }

  @Selector()
  static getClientsLoadable({ clients }: PaymentsStateModel): Loadable<Client[]> {
    return clients;
  }

  @Selector([PaymentsState.getClientsLoadable])
  static getClientsStatus({ status }: Loadable<Client[]>): LoadableStatus {
    return status;
  }

  @Selector([PaymentsState.getClientsLoadable])
  static getClients({ data }: Loadable<Client[]>): Client[] {
    return data ?? [];
  }

  @Selector()
  static getJobsLoadable({ jobs }: PaymentsStateModel): Loadable<BaseJob[]> {
    return jobs;
  }

  @Selector([PaymentsState.getJobsLoadable])
  static getJobsStatus({ status }: Loadable<BaseJob[]>): LoadableStatus {
    return status;
  }

  @Selector([PaymentsState.getJobsLoadable])
  static getJobs({ data }: Loadable<BaseJob[]>): BaseJob[] {
    return data ?? [];
  }

  @Selector()
  static getShouldSaveNewPaymentMethod({ shouldSaveNewPaymentMethod }: PaymentsStateModel): boolean {
    return shouldSaveNewPaymentMethod;
  }

  @Selector()
  static getShouldSetNewPaymentMethodAsDefault({ shouldSetNewPaymentMethodAsDefault }: PaymentsStateModel): boolean {
    return shouldSetNewPaymentMethodAsDefault;
  }

  @Selector()
  static getLateFeesLoadable({ lateFees }: PaymentsStateModel): Loadable<BaseLateFee[]> {
    return lateFees;
  }

  @Selector([PaymentsState.getLateFeesLoadable])
  static getLateFeesStatus({ status }: Loadable<BaseLateFee[]>): LoadableStatus {
    return status;
  }

  @Selector([PaymentsState.getLateFeesLoadable])
  static getLateFees(lateFeesLoadable: Loadable<BaseLateFee[]>): BaseLateFee[] {
    return lateFeesLoadable?.data ?? [];
  }

  @Selector()
  static getPayAllLateFees({ payAllLateFees }: PaymentsStateModel): boolean {
    return payAllLateFees;
  }

  @Selector()
  static getPayLateFeeClientIds({ payLateFeeClientIds }: PaymentsStateModel): number[] {
    return payLateFeeClientIds ?? [];
  }

  @Selector([
    PaymentsState.getActiveCurrency,
    PaymentsState.doAppliedCreditsCoverArTranAllocations,
    PaymentsState.getPayWithInvoices,
  ])
  static getCanPayLateFees(
    activeCurrency: Currency,
    doCreditsCoverArTranAllocations: boolean,
    payWithInvoices: boolean,
  ): boolean {
    return activeCurrency.code === CurrencyCode.USD && (payWithInvoices || !doCreditsCoverArTranAllocations);
  }

  @Selector([
    LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_APPLY_CREDITS_TO_LATE_FEES),
    PaymentsState.getPayWithInvoices,
    PaymentsState.getLateFees,
    PaymentsState.getPayAllLateFees,
    PaymentsState.getPayLateFeeClientIds,
  ])
  static getSelectedLateFees(
    payLateFeesByClientEnabled: boolean,
    payWithInvoices: boolean,
    lateFees: BaseLateFee[],
    payLateFees: boolean,
    payLateFeeClientIds: number[],
  ): BaseLateFee[] {
    if (!payLateFeesByClientEnabled || !payWithInvoices) {
      return payLateFees ? lateFees : [];
    }

    return isEmpty(payLateFeeClientIds) ? [] : lateFees.filter(({ clientId }) => payLateFeeClientIds.includes(clientId));
  }

  @Selector([PaymentsState.getSelectedLateFees])
  static getSelectedLateFeeIds(lateFees: BaseLateFee[]): number[] {
    return lateFees.map(({ id }) => id);
  }

  /**
   * At the moment, only used to show the late fees total in the payment summary, which still shows when opting to not pay late fees.
   * @param lateFees
   */
  @Selector([PaymentsState.getLateFees])
  static getLateFeesTotal(lateFees: BaseLateFee[]): number {
    const total = sumBy(lateFees, 'amount');
    return roundMonetaryValue(total);
  }

  @Selector([PaymentsState.getSelectedLateFees])
  static getSelectedLateFeesTotal(lateFees: BaseLateFee[]): number {
    const total = sumBy(lateFees, 'amount');
    return roundMonetaryValue(total);
  }

  /**
   * At time of creation, used solely for determining processing/international fees and, in turn, payment total.
   */
  @Selector([
    LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_APPLY_CREDITS_TO_LATE_FEES),
    PaymentsState.getPayWithInvoices,
    PaymentsState.getSelectedLateFees,
  ])
  static getSelectedStandaloneLateFees(
    enablePayLateFeesByClient: boolean,
    payWithInvoices: boolean,
    selectedLateFees: BaseLateFee[],
  ): BaseLateFee[] {
    return enablePayLateFeesByClient && payWithInvoices ? [] : selectedLateFees;
  }

  @Selector([PaymentsState.getSelectedStandaloneLateFees])
  static getSelectedStandaloneLateFeesTotal(lateFees: BaseLateFee[]): number {
    const total = sumBy(lateFees, 'amount');
    return roundMonetaryValue(total);
  }

  /**
   * At time of creation, used solely for determining which late fees have an outstanding balance after credits have been applied (which
   * prioritizes late fees). The IDs of those late fees are passed in the initial charge payload (to mark as to be paid).
   */
  @Selector([
    LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_APPLY_CREDITS_TO_LATE_FEES),
    PaymentsState.getCreditAllocations,
    PaymentsState.getSelectedLateFees,
  ])
  static getRemainingSelectedLateFees(
    enablePayLateFeesByClient: boolean,
    creditAllocations: CreditAllocation[],
    selectedLateFees: BaseLateFee[],
  ): BaseLateFee[] {
    if (!enablePayLateFeesByClient || isEmpty(creditAllocations)) {
      return selectedLateFees;
    }

    return selectedLateFees.reduce(
      (acc, lateFee) => {
        const amountRemaining = roundMonetaryValue(
          lateFee.amount + sumBy(creditAllocations.filter(({ arTranId }) => arTranId === lateFee.id), 'allocated'),
        );

        return amountRemaining > 0 ? acc.concat({ ...lateFee, amount: amountRemaining }) : acc;
      },
      [],
    );
  }

  @Selector()
  static getPaymentFeeConfigsLoadable({ paymentFeeConfigs }: PaymentsStateModel): Loadable<PaymentFeeConfig[]> {
    return paymentFeeConfigs;
  }

  @Selector([PaymentsState.getPaymentFeeConfigsLoadable])
  static getPaymentFeeConfigsStatus({ status }: Loadable<PaymentFeeConfig[]>): LoadableStatus {
    return status;
  }

  @Selector([PaymentsState.getPaymentFeeConfigsLoadable])
  static getPaymentFeeConfigs(paymentFeeConfigsLoadable: Loadable<PaymentFeeConfig[]>): PaymentFeeConfig[] {
    return paymentFeeConfigsLoadable?.data ?? [];
  }

  static getPaymentFeeConfig(
    type: PaymentFeeType,
    paymentMethodType: PaymentFeePaymentMethodType,
    currencyCode: CurrencyCode,
  ): (paymentFeeConfigs: PaymentFeeConfig[], configs: ConfigModel) => PaymentFeeConfig {
    return createSelector(
      [PaymentsState.getPaymentFeeConfigs],
      paymentFeeConfigs => paymentFeeConfigs.find(config =>
        areStringsEqualIgnoreCase(config.type, type)
        && areStringsEqualIgnoreCase(config.paymentMethodType, paymentMethodType)
        && areStringsEqualIgnoreCase(config.currencyCode, currencyCode),
      ),
    );
  }

  static getPaymentFeeRate(
    type: PaymentFeeType,
    paymentMethodType: PaymentFeePaymentMethodType,
    currencyCode: CurrencyCode,
  ): (paymentFeeConfig: PaymentFeeConfig, configs: ConfigModel) => number {
    return createSelector(
      [
        PaymentsState.getPaymentFeeConfig(type, paymentMethodType, currencyCode),
        ConfigState.getConfigs,
      ],
      (paymentFeeConfig, configs) => paymentFeeConfig?.value ?? PaymentsState._getPaymentFeeFallback(configs, type),
    );
  }

  static getPaymentFeePaymentMethodType(paymentMethod: AbstractPaymentMethod): PaymentFeePaymentMethodType {
    if (paymentMethod == undefined) {
      return undefined;
    }

    if (paymentMethod.type === PaymentMethodType.CARD) {
      return [CardFunding.CREDIT, CardFunding.DEBIT, CardFunding.PREPAID].includes(paymentMethod.subType as CardFunding)
        ? paymentMethod.subType as PaymentFeePaymentMethodType
        : undefined;
    }

    switch (paymentMethod.type) {
      case PaymentMethodType.ACH:
      case PaymentMethodType.US_BANK_ACCOUNT:
        return PaymentMethodType.ACH;
      case PaymentMethodType.ACSS:
      case PaymentMethodType.ACSS_DEBIT:
        return PaymentMethodType.ACSS;
      case PaymentMethodType.BACS:
      case PaymentMethodType.BACS_DEBIT:
        return PaymentMethodType.BACS;
      default:
        throw new Error(`Unknown payment method type: ${paymentMethod.type}`);
    }
  }

  private static _getPaymentFeeFallback(configs: ConfigModel, type: PaymentFeeType): number {
    switch (type) {
      case PaymentFeeType.CREDIT_CARD_PROCESSING_FEE_RATE:
        return configs.customerCreditCardProcessingFeeRate;
      case PaymentFeeType.INTERNATIONAL_FEE_RATE:
        return configs.internationalCardFeeRate;
      case PaymentFeeType.PAYMENT_PLAN_CREDIT_CARD_PROCESSING_FEE_RATE:
        return configs.paymentPlanProcessingFeeRate;
      default:
        throw new Error(`Unknown payment fee type: ${type}`);
    }
  }

  @Selector()
  static getPayWithoutInvoicePayable(
    { payWithoutInvoicePayable }: PaymentsStateModel,
  ): Partial<PayWithoutInvoicePayable | PayOpenPortalPayable> {
    return payWithoutInvoicePayable;
  }

  @Selector([
    PaymentsState.getPayWithInvoices,
    PaymentsState.getPayWithoutInvoicePayable,
    PaymentsState.getClientPaymentSummaries,
  ])
  static getSubtotal(
    payWithInvoices: boolean,
    payWithoutInvoicePayable: Partial<PayWithoutInvoicePayable | PayOpenPortalPayable>,
    clientPaymentSummaries: ClientPaymentSummary[],
  ): number {
    if (!payWithInvoices) {
      return payWithoutInvoicePayable?.subtotal;
    }

    const subtotal = sumBy(clientPaymentSummaries, 'subtotal');
    const appliedCreditTotal = sumBy(clientPaymentSummaries, 'creditApplied');
    return (subtotal === 0 || isNaN(subtotal)) && appliedCreditTotal === 0 ? undefined : roundMonetaryValue(subtotal);
  }

  @Selector()
  static getCustomProcessingFeeRate({ customProcessingFeeRate }: PaymentsStateModel): number {
    return customProcessingFeeRate;
  }

  static shouldApplyProcessingFee(selectedPaymentMethod: AbstractPaymentMethod): (_: UsState) => boolean {
    return createSelector(
      [CommonSettingsState.getUsState(selectedPaymentMethod?.billingDetails.address.state as UsStateAbbreviation)],
      paymentMethodUsState => !!(selectedPaymentMethod?.isCreditCard && !paymentMethodUsState?.optOutOfProcessingFee),
    );
  }

  static getProcessingFee(
    selectedPaymentMethod: AbstractPaymentMethod,
    options: GetTotalOptions = {},
  ): (...params: [number, number, number, number, boolean]) => number {
    return createSelector(
      [
        PaymentsState.getSubtotal,
        PaymentsState.getSelectedStandaloneLateFeesTotal,
        PaymentsState.getCustomProcessingFeeRate,
        PaymentsState.getPaymentFeeRate(
          options.isPaymentPlan
            ? PaymentFeeType.PAYMENT_PLAN_CREDIT_CARD_PROCESSING_FEE_RATE
            : PaymentFeeType.CREDIT_CARD_PROCESSING_FEE_RATE,
          PaymentsState.getPaymentFeePaymentMethodType(selectedPaymentMethod),
          options.currencyCode,
        ),
        PaymentsState.shouldApplyProcessingFee(selectedPaymentMethod),
      ],
      (standardSubtotal, defaultLateFeesTotal, adminProcessingFeeRate, standardProcessingFeeRate, shouldApplyProcessingFee) => {
        const subtotal = options.subtotal ?? standardSubtotal;
        const processingFeeRate = options.isPaymentPlan ? standardProcessingFeeRate : (adminProcessingFeeRate ?? standardProcessingFeeRate);
        const lateFeesTotal = options.ignoreLateFees || (options.currencyCode && options.currencyCode !== CurrencyCode.USD)
          ? 0
          : defaultLateFeesTotal;

        if (
          selectedPaymentMethod == undefined
          || subtotal == undefined
          || processingFeeRate == undefined
        ) {
          return undefined;
        }

        if (!shouldApplyProcessingFee) {
          return 0;
        }

        const rawProcessingFee = (subtotal + lateFeesTotal) * processingFeeRate;
        return roundMonetaryValue(rawProcessingFee);
      },
    );
  }

  static getInternationalFee(
    selectedPaymentMethod: AbstractPaymentMethod,
    options: GetTotalOptions = {},
  ): (...params: [number, number, BusinessEntity, number]) => number {
    return createSelector(
      [
        PaymentsState.getSubtotal,
        PaymentsState.getSelectedStandaloneLateFeesTotal,
        options.businessEntityId
          ? ConfigState.getBusinessEntity(options.businessEntityId)
          : PaymentsState.getSelectedBusinessEntity(true),
        PaymentsState.getPaymentFeeRate(
          PaymentFeeType.INTERNATIONAL_FEE_RATE,
          PaymentsState.getPaymentFeePaymentMethodType(selectedPaymentMethod),
          options.currencyCode,
        ),
      ],
      (defaultSubtotal, defaultLateFeesTotal, selectedBusinessEntity, internationalFeeRate) => {
        if (selectedPaymentMethod == undefined) {
          return undefined;
        }

        if (normalizeCountryAlpha2Code(selectedPaymentMethod.country) === selectedBusinessEntity?.countryCode) {
          return 0;
        }

        const subtotal = options.subtotal ?? defaultSubtotal;
        const lateFeesTotal = options.ignoreLateFees || (options.currencyCode && options.currencyCode !== CurrencyCode.USD)
          ? 0
          : defaultLateFeesTotal;
        const rawInternationalFee = (subtotal + lateFeesTotal) * internationalFeeRate;
        return roundMonetaryValue(rawInternationalFee);
      },
    );
  }

  static getTotal(
    selectedPaymentMethod: AbstractPaymentMethod,
    options: GetTotalOptions = {},
  ): (...params: [number, number, number, number]) => number {
    return createSelector(
      [
        PaymentsState.getSubtotal,
        PaymentsState.getSelectedStandaloneLateFeesTotal,
        PaymentsState.getProcessingFee(selectedPaymentMethod, options),
        PaymentsState.getInternationalFee(selectedPaymentMethod, options),
      ],
      (defaultSubtotal, defaultLateFeesTotal, processingFee, internationalFee) => {
        if (selectedPaymentMethod == undefined) {
          return undefined;
        }

        const subtotal = options.subtotal ?? defaultSubtotal;
        const lateFeesTotal = options.ignoreLateFees || (options.currencyCode && options.currencyCode !== CurrencyCode.USD)
          ? 0
          : defaultLateFeesTotal;
        const total = subtotal + lateFeesTotal + processingFee + internationalFee;
        return Number.isNaN(total) ? undefined : roundMonetaryValue(total);
      },
    );
  }

  @Selector()
  static getArTranAllocations(state: PaymentsStateModel): ArTranAllocation[] {
    return state?.arTranAllocations;
  }

  @Selector([PaymentsState.getArTranAllocations, PaymentsState.getPayables])
  static getArTranAllocationsWithClientIds(
    arTranAllocations: ArTranAllocation[],
    payables: PayableTableObject[],
  ): ArTranAllocationWithClientId[] {
    return (arTranAllocations ?? [])
      .map(arTranAllocation => {
        const matchingPayable = (payables ?? []).find(({ arTranId }) => arTranId === arTranAllocation.arTranId);

        return matchingPayable
          ? { ...arTranAllocation, clientId: matchingPayable.invoice.arTran.clientId }
          : undefined;
      })
      .filter(Boolean);
  }

  @Selector([PaymentsState.getArTranAllocations])
  static getArTranAllocationsTotal(arTranAllocations: ArTranAllocation[]): number {
    return (arTranAllocations ?? []).reduce((acc, { allocatedAmount }) => acc + allocatedAmount, 0);
  }

  @Selector([PaymentsState.getArTranAllocations, PaymentsState.getPayables])
  static hasInvalidArTranAllocations(
    arTranAllocations: ArTranAllocation[],
    payables: PayableTableObject[],
  ): boolean {
    return (arTranAllocations ?? []).some(({ arTranId, allocatedAmount }) => {
      if (!(allocatedAmount > 0)) {
        return true;
      }

      // An allocation can outlive its payable: GetPayables drops `data` while refreshing, and the refreshed
      // list filters out paid invoices. Without a payable there is no balance to compare against, so the
      // check degrades to the positive-amount guard rather than blocking Pay on a transient loading state.
      const payable = (payables ?? []).find(candidate => candidate.arTranId === arTranId);
      return payable != undefined && roundMonetaryValue(allocatedAmount) > roundMonetaryValue(payable.amountRemaining);
    });
  }

  static getSelectedBusinessEntity(
    shouldFallbackToDefault: boolean = false,
  ): (...params: [boolean, PayableTableObject[], BusinessEntity[], BusinessEntity]) => BusinessEntity {
    return createSelector(
      [
        PaymentsState.getPayWithInvoices,
        PaymentsState.getSelectedPayables,
        ConfigState.getBusinessEntities,
        ConfigState.getDefaultBusinessEntity,
      ],
      (payWithInvoices, selectedPayables, businessEntities, defaultBusinessEntity) => {
        if (!payWithInvoices || isEmpty(selectedPayables)) {
          return shouldFallbackToDefault ? defaultBusinessEntity : undefined;
        }

        // should always fall back to default in this situation
        const selectedBusinessEntityId = selectedPayables[0].entityId;
        return businessEntities.find(({ id }) => id === selectedBusinessEntityId) ?? defaultBusinessEntity;
      },
    );
  }

  @Selector([PaymentsState.getCustomProcessingFeeRate, ConfigState.getCustomerCreditCardProcessingFeeRate])
  static getProcessingFeeRateOverridden(overriddenProcessingFeeRate: number, customerCreditCardProcessingFeeRate: number): boolean {
    return overriddenProcessingFeeRate != undefined && overriddenProcessingFeeRate !== customerCreditCardProcessingFeeRate;
  }

  @Selector()
  static getExecutedPaymentBatches({ executedPaymentBatches }: PaymentsStateModel): ExecutedPaymentBatches {
    return executedPaymentBatches;
  }

  @Selector([PaymentsState.getExecutedPaymentBatches])
  static getLastExecutedPaymentBatchId(executedPaymentBatches: ExecutedPaymentBatches): number {
    if (isEmpty(executedPaymentBatches)) {
      return undefined;
    }

    const executedPaymentBatchIds = Object.keys(executedPaymentBatches).map(Number);
    return Math.max(...executedPaymentBatchIds);
  }

  @Selector([PaymentsState.getExecutedPaymentBatches, PaymentsState.getLastExecutedPaymentBatchId])
  static getLastExecutedPaymentBatch(executedPaymentBatches: ExecutedPaymentBatches, lastExecutedPaymentId: number): ExecutedPaymentBatch {
    return executedPaymentBatches?.[lastExecutedPaymentId];
  }

  @Selector([PaymentsState.getLastExecutedPaymentBatch])
  static getLastExecutedPaymentBatchStatus(lastExecutedPayment: ExecutedPaymentBatch): LoadableStatus {
    return PaymentsState.getExecutedPaymentBatchStatusHelper(lastExecutedPayment);
  }

  @Selector([PaymentsState.getLastExecutedPaymentBatch])
  static isLastExecutedPaymentBatchComplete(lastExecutedPayment: ExecutedPaymentBatch): boolean {
    if (lastExecutedPayment == undefined) {
      return undefined;
    }

    if (lastExecutedPayment.initial.status === LoadableStatus.ERROR) {
      return true;
    }

    return ![lastExecutedPayment.initial.status as LoadableStatus]
      .concat('addOns' in lastExecutedPayment ? lastExecutedPayment.addOns.map(({ status }) => status) : [])
      .some(status => LoadableHelper.isLoading(status));
  }

  static getExecutedPaymentBatchStatusHelper(executedPayment: ExecutedPaymentBatch): LoadableStatus {
    if (executedPayment == undefined) {
      return undefined;
    }

    if (!('addOns' in executedPayment)) {
      return executedPayment.initial.status;
    }

    const addOnStatuses = executedPayment.addOns.map(({ status }) => status);
    return LoadableHelper.coalesceStatus([executedPayment.initial.status].concat(addOnStatuses));
  }

  static getExecutedPaymentBatchStatus(executedPaymentBatchId: number): (executedPaymentBatches: ExecutedPaymentBatches) => LoadableStatus {
    return createSelector(
      [PaymentsState.getExecutedPaymentBatches],
      executedPaymentBatches => PaymentsState.getExecutedPaymentBatchStatusHelper(executedPaymentBatches?.[executedPaymentBatchId]),
    );
  }

  static getExecutedPaymentBatch(executedPaymentBatchId: number): (executedPaymentBatches: ExecutedPaymentBatches) => ExecutedPaymentBatch {
    return createSelector(
      [PaymentsState.getExecutedPaymentBatches],
      executedPaymentBatches => executedPaymentBatches?.[executedPaymentBatchId],
    );
  }

  @Selector([PaymentsState.getLastExecutedPaymentBatch])
  static isLastExecutedPaymentBatchCancelled(lastExecutedPaymentBatch: ExecutedPaymentBatch): boolean {
    return lastExecutedPaymentBatch === null;
  }

  @Selector([PaymentsState.getLastExecutedPaymentBatch])
  static getCurrentPaymentLoadable(lastExecutedPaymentBatch: ExecutedPaymentBatch): Loadable<ChargePayload | ApplyCreditsPayload> {
    if (!('addOns' in lastExecutedPaymentBatch)) {
      return lastExecutedPaymentBatch.initial;
    }

    return lastExecutedPaymentBatch.addOns.find(({ status }) => status === LoadableStatus.LOADING);
  }

  @Selector([PaymentsState.getCurrentPaymentLoadable])
  static getCurrentPaymentStatus({ status }: Loadable<ChargePayload | ApplyCreditsPayload>): LoadableStatus {
    return status;
  }

  @Selector([PaymentsState.getCurrentPaymentLoadable])
  static getCurrentPayment({ data }: Loadable<ChargePayload | ApplyCreditsPayload>): ChargePayload | ApplyCreditsPayload {
    return data;
  }

  @Selector()
  static getCurrentPaymentActionRequirement({ currentPaymentActionRequirement }: PaymentsStateModel): PaymentActionRequirement {
    return currentPaymentActionRequirement;
  }

  @Selector()
  static getCurrentPaymentBatchId({ currentPaymentBatchId }: PaymentsStateModel): string | undefined {
    return currentPaymentBatchId;
  }

  @Selector()
  static getPaymentEntrySource({ paymentEntrySource }: PaymentsStateModel): PaymentSurface | undefined {
    return paymentEntrySource;
  }

  @Selector([PaymentsState.getCurrentPaymentActionRequirement])
  static getCurrentPaymentCancelled(currentPaymentActionRequirement: PaymentActionRequirement): boolean {
    return !!currentPaymentActionRequirement?.cancelled;
  }

  @Selector()
  static getProvidedPrimaryRecipientEmail({ providedPrimaryRecipientEmail }: PaymentsStateModel): string {
    return providedPrimaryRecipientEmail;
  }

  static getPrimaryRecipientEmailLogic(providedPrimaryRecipientEmail: string, userEmail: string): string {
    return userEmail ?? providedPrimaryRecipientEmail;
  }

  getPrimaryRecipientEmail(): Observable<string> {
    return combineLatest([
      this.authStateAdaptor.getUserEmail(),
      this.store.select(PaymentsState.getProvidedPrimaryRecipientEmail),
    ]).pipe(
      map(([userEmail, providedPrimaryRecipientEmail]) =>
        PaymentsState.getPrimaryRecipientEmailLogic(providedPrimaryRecipientEmail, userEmail),
      ),
    );
  }

  getPrimaryRecipientEmailSnapshot(): string {
    return PaymentsState.getPrimaryRecipientEmailLogic(
      this.store.selectSnapshot(PaymentsState.getProvidedPrimaryRecipientEmail),
      this.authStateAdaptor.getUserEmailSnapshot(),
    );
  }

  @Selector()
  static getAdditionalRecipientEmails({ additionalRecipientEmails }: PaymentsStateModel): string[] {
    return additionalRecipientEmails ?? [];
  }

  getAllRecipientEmailsSnapshot(): string[] {
    // Drop falsy entries so an unresolved primary email can't serialize as [null] (rejected backend-side).
    return [this.getPrimaryRecipientEmailSnapshot()]
      .concat(this.store.selectSnapshot(PaymentsState.getAdditionalRecipientEmails) ?? [])
      .filter(Boolean);
  }

  @Selector([PaymentsState.getPayWithInvoices, PaymentsState.getSelectedPayables])
  static getActiveCurrency(payWithInvoices: boolean, selectedPayables: PayableTableObject[]): Currency {
    return payWithInvoices && selectedPayables.find(({ currency }) => currency)?.currency || DEFAULT_CURRENCY;
  }

  @Selector()
  static getQuickPaymentLinkIdLoadable({ quickPaymentLinkId }: PaymentsStateModel): Loadable<number> {
    return quickPaymentLinkId;
  }

  @Selector([PaymentsState.getQuickPaymentLinkIdLoadable])
  static getQuickPaymentLinkIdStatus(quickPaymentLinkIdLoadable: Loadable<number>): LoadableStatus {
    return quickPaymentLinkIdLoadable?.status;
  }

  @Selector()
  static getQuickPaymentLinkArgs({ quickPaymentLinkArgs }: PaymentsStateModel): OpenPaymentLinkResult | undefined {
    return quickPaymentLinkArgs;
  }

  @Selector([PaymentsState.getQuickPaymentLinkIdLoadable])
  static getQuickPaymentLinkId(quickPaymentLinkIdLoadable: Loadable<number>): number {
    return quickPaymentLinkIdLoadable?.data;
  }

  static getBaseIdempotencyKey(tenant: string, selectedPaymentMethod: AbstractPaymentMethod, total: number): string {
    // updates every 5 minutes since epoch (10:41:52 => 10:40:00)
    const timeWindow = round(Math.floor(Date.now() / IDEMPOTENCY_KEY_TIME_WINDOW) * IDEMPOTENCY_KEY_TIME_WINDOW, 0);

    return [tenant, roundMonetaryValue(total), selectedPaymentMethod.id, timeWindow].join(IDEMPOTENCY_KEY_DELIMITER);
  }

  static getInvoicesIdempotencyKey(
    tenant: string,
    selectedPaymentMethod: AbstractPaymentMethod,
    total: number,
    arTranAllocations: ArTranAllocation[],
  ): string {
    const baseIdempotencyKey = PaymentsState.getBaseIdempotencyKey(tenant, selectedPaymentMethod, total);
    const arTranAllocationsString = Array.from(arTranAllocations)
      .sort((a, b) => a.arTranId - b.arTranId)
      .map(({ arTranId, allocatedAmount }) => [arTranId, roundMonetaryValue(allocatedAmount)].join(':'))
      .join('|');

    return PaymentsState._appendFinalSectionToIdempotencyKey(baseIdempotencyKey, arTranAllocationsString);
  }

  static getWithoutInvoicesIdempotencyKey(
    tenant: string,
    selectedPaymentMethod: AbstractPaymentMethod,
    total: number,
    { clientId, typeId, jobId, companyName, notes }: PayWithoutInvoicePayable & PayOpenPortalPayable,
  ): string {
    const baseIdempotencyKey = PaymentsState.getBaseIdempotencyKey(tenant, selectedPaymentMethod, total);
    const payWithoutInvoicePayableString = [clientId, typeId, jobId, companyName].filter(Boolean).join('|');

    const idempotencyKey = [baseIdempotencyKey, payWithoutInvoicePayableString].join(IDEMPOTENCY_KEY_DELIMITER);
    return PaymentsState._appendFinalSectionToIdempotencyKey(idempotencyKey, notes);
  }

  /**
   * Appends a final section to the idempotency key, ensuring it does not exceed the maximum length.
   *
   * If overflowing, it hashes the final section and truncates it to fit within the maximum length (accounting for delimiters).
   * - `'---------------'` with a hash of `12345` would become `'---------_12345'`.
   *
   * @param idempotencyKey
   * @param finalSection
   * @private
   */
  private static _appendFinalSectionToIdempotencyKey(idempotencyKey: string, finalSection: string): string {
    if (idempotencyKey.length + finalSection.length < IDEMPOTENCY_KEY_MAX_LENGTH) {
      return `${idempotencyKey}${IDEMPOTENCY_KEY_DELIMITER}${finalSection}`;
    }

    // uh oh...verflow
    const finalSectionHash = hashCode(finalSection);
    const unhashedFinalSectionLength = IDEMPOTENCY_KEY_MAX_LENGTH
      - idempotencyKey.length // preserve entire existing idempotency key
      - `${finalSectionHash}`.length // account for the entire hash
      - IDEMPOTENCY_KEY_DELIMITER.length // account for the delimiter added between the existing idempotency key and the final section
      - 1; // leave room for the underscore before the hash

    const truncatedFinalSection = `${finalSection.slice(0, unhashedFinalSectionLength)}_${finalSectionHash}`;
    return `${idempotencyKey}${IDEMPOTENCY_KEY_DELIMITER}${truncatedFinalSection}`;
  }

  /**
   * Build the common metadata envelope attached to every PaymentWorkflowEvent. `selectedPaymentMethod`
   * is optional because some fire sites (e.g. unpaid-page rehydration) emit before the user has picked one.
   * `surface` is inferred from the current pathname; `paymentBatchId` is only populated for events that
   * fire during an in-flight make-payment lifecycle (set by `MakePayment`, cleared by
   * `ClearSuccessfulPaymentData`).
   */
  static buildPaymentEventBase(store: Store, selectedPaymentMethod?: AbstractPaymentMethod): PaymentEventBaseMetadata {
    const payWithInvoices = store.selectSnapshot(PaymentsState.getPayWithInvoices);
    const activeCurrency = store.selectSnapshot(PaymentsState.getActiveCurrency);
    const paymentBatchId = store.selectSnapshot(PaymentsState.getCurrentPaymentBatchId);

    const paymentType: PaymentType = payWithInvoices ? 'invoice' : 'non-invoice';

    const paymentMethodType = selectedPaymentMethod
      ? (selectedPaymentMethod.subType ?? selectedPaymentMethod.type ?? '').toString().toUpperCase()
      : undefined;

    return {
      paymentType,
      paymentMethodType,
      currency: activeCurrency?.code ?? DEFAULT_CURRENCY.code,
      surface: PaymentsState.resolvePaymentSurface(store),
      ...(paymentBatchId ? { paymentBatchId } : {}),
    };
  }

  /**
   * Map the active route to a `PaymentSurface`. Order matters: the open-portal / quick-payment-link
   * route lives at `/{ROUTE_TOKEN_QUICK_PAYMENTS}` (top level, public), distinct from the authenticated
   * payments page at `/main/{ROUTE_TOKEN_PAYMENTS}`, so we anchor the check to a leading slash to avoid
   * matching the authenticated route. 'engagement' is reserved as a placeholder for an upcoming entry
   * point; we already classify the route so dashboards work the moment that surface starts firing events.
   *
   * PP-3775: the `/reviewAndPay` URL is reached both by the pay-invoice email link (via
   * `PayInvoiceAuthGuard`) and the in-portal "Review Invoices" button, so the pathname alone can't tell
   * them apart. When a `store` is supplied we prefer the persisted `paymentEntrySource` override on that
   * one branch — the entry chokepoints set it to `'pay-invoice-email-invoice'`, `'pay-invoice-email-collections'`
   * (ART-8293), or `'review-and-pay'`. The override is consulted only here; every other surface stays pure
   * URL classification. `store` is optional so the URL-only callers (e.g. `resolvePaymentMethodSurfaceFromUrl`)
   * keep their existing behavior.
   */
  static resolvePaymentSurface(store?: Store): PaymentSurface {
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    if (pathname === `/${ROUTE_TOKEN_QUICK_PAYMENTS}` || pathname.startsWith(`/${ROUTE_TOKEN_QUICK_PAYMENTS}/`)) {
      return 'quick-payment-link';
    }
    if (pathname.includes(ROUTE_TOKEN_REVIEW_AND_PAY)) {
      const entrySourceOverride = store?.selectSnapshot(PaymentsState.getPaymentEntrySource);
      return entrySourceOverride === 'pay-invoice-email-invoice' || entrySourceOverride === 'pay-invoice-email-collections'
        ? entrySourceOverride
        : 'review-and-pay';
    }
    if (pathname.includes(ROUTE_TOKEN_ENGAGEMENTS_BASE)) {
      return 'engagement';
    }
    return 'invoices';
  }

  /**
   * URL-derived `PaymentMethodSurface` for hosts that wrap `SelectPaymentMethodComponent` without
   * pinning their own intent/flow at the host site — namely `PaymentsSidebarComponent` and
   * `OpenPortalComponent`. Reuses `resolvePaymentSurface()` so URL classification stays the single
   * source of truth (the gaps doc explicitly warns against a parallel resolver). `engagement` is
   * mapped to `engagement-payment-info-step` because the engagement signing step hosts the select
   * component directly and doesn't go through the sidebar — settings hosts (payment-plan-modal,
   * autopay-dialog) pin their surfaces at the host site and bypass this helper.
   */
  static resolvePaymentMethodSurfaceFromUrl(): PaymentMethodSurface {
    const paymentSurface = PaymentsState.resolvePaymentSurface();
    if (paymentSurface === 'engagement') {
      return 'engagement-payment-info-step';
    }
    return `payments-sidebar-${paymentSurface}` as PaymentMethodSurface;
  }

  constructor(
    private readonly store: Store,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly paymentPlatformService: PaymentPlatformService,
    private readonly invoiceApiService: InvoiceApiService,
    private readonly additionalServiceApiService: AdditionalServiceApiService,
    private readonly jobApiService: JobApiService,
    private readonly clientApiService: ClientApiService,
    private readonly feeApiService: FeeApiService,
    private readonly authService: AuthService,
    private readonly configService: ConfigService,
    private readonly creditApiService: CreditApiService,
    private readonly paymentsApiService: PaymentsApiService,
    private readonly logger: Logger,
    private readonly authAdaptor: AuthAdaptorService,
    private readonly authStateAdaptor: AuthStateAdaptorService,
    private readonly globals: Globals,
  ) {}

  @Action([LogIn])
  onLogIn({ dispatch }: StateContext<PaymentsStateModel>): Observable<unknown> {
    this.logger.info('PAYMENT : PaymentsState : onLogIn');
    return dispatch(new SetDefaultActivePlatform());
  }

  //#region Base Actions
  @Action([SetDefaultActivePlatform])
  setDefaultActivePlatform({ patchState }: StateContext<PaymentsStateModel>): Observable<PaymentPlatform> {
    const activePlatformStatus = this.store.selectSnapshot(PaymentsState.getActivePlatformStatus);
    if (!LoadableHelper.shouldRequest(activePlatformStatus)) {
      return;
    }

    this.logger.info('PAYMENT : PaymentsState : setDefaultActivePlatform');
    patchState({ activePlatform: { status: LoadableStatus.LOADING } });

    return this.paymentPlatformService.setDefaultActivePlatformService().pipe(
      setLoadableInState(patchState, 'activePlatform'),
      take(1),
    );
  }

  @Action([SetActivePlatform])
  setActivePlatform({ patchState }: StateContext<PaymentsStateModel>, { activePlatform }: SetActivePlatform): Observable<PaymentPlatform> {
    this.logger.info('PAYMENT : PaymentsState : setActivePlatform', activePlatform);
    return this.paymentPlatformService.setActivePlatformService(activePlatform).pipe(
      mapTo(activePlatform),
      setLoadableInState(patchState, 'activePlatform'),
      take(1),
    );
  }

  @Action([GetAddOnOptions])
  getAddOnOptions({ patchState }: StateContext<PaymentsStateModel>): Observable<AddOnOption[]> {
    const payablesStatus = this.store.selectSnapshot(PaymentsState.getAddOnOptionsStatus);
    if (!LoadableHelper.shouldRequest(payablesStatus)) {
      return;
    }

    patchState({ addOnOptions: { status: LoadableStatus.LOADING } });

    return this.additionalServiceApiService.getAddOnOptions().pipe(
      setLoadableInState(patchState, 'addOnOptions'),
      take(1),
    );
  }

  @Action([AddAddOn])
  addAddOn({ patchState }: StateContext<PaymentsStateModel>, { addOn }: AddAddOn): void {
    const addOns = this.store.selectSnapshot(PaymentsState.getAddOns);

    patchState({ addOns: addOns.concat({ ...addOn, id: addOns.length }) });
  }

  @Action([RemoveAddOn])
  removeAddOn({ patchState }: StateContext<PaymentsStateModel>, { addOnId }: RemoveAddOn): void {
    const existingAddOns = this.store.selectSnapshot(PaymentsState.getAddOns);
    const addOns = removeEntityByEntry(existingAddOns, 'id', addOnId);

    patchState({ addOns });
  }

  @Action([GetPayables])
  getPayables(
    { patchState }: StateContext<PaymentsStateModel>,
    { forceRefresh }: GetPayables,
  ): Observable<[PayableUi[], AutoPay[]]> {
    const payablesStatus = this.store.selectSnapshot(PaymentsState.getRawPayablesStatus);
    if (!(LoadableHelper.shouldRequest(payablesStatus) || forceRefresh)) {
      return;
    }

    patchState({ payables: { status: LoadableStatus.LOADING } });

    return combineLatest([
      this.invoiceApiService.getInvoices().pipe(
        // TODO: We may want to store ALL payables at some point. If so, this map should be removed and new selectors should be made (paid vs unpaid).
        map(invoices => invoices.filter(({ isPaid }) => !isPaid)),
        setLoadableInState(patchState, 'payables'),
      ),
      getLoadedResults(this.store.select(CommonSettingsState.getAutoPayClientsLoadable)),
    ]).pipe(
      take(1),
    );
  }

  @Action([GetCreditLabels])
  getCreditLabels({ patchState }: StateContext<PaymentsStateModel>, { forceRefresh }: GetCreditLabels): Observable<CreditLabel[]> {
    const creditLabelsStatus = this.store.selectSnapshot(PaymentsState.getCreditLabelsStatus);
    if (!(LoadableHelper.shouldRequest(creditLabelsStatus) || forceRefresh)) {
      return getLoadedResults(this.store.select(PaymentsState.getCreditLabelsLoadable), true).pipe(take(1));
    }

    patchState({ creditLabels: { status: LoadableStatus.LOADING } });

    return this.creditApiService.getCreditLabels().pipe(
      setLoadableInState(patchState, 'creditLabels'),
    );
  }

  @Action([GetCredits])
  getCredits({ patchState, dispatch }: StateContext<PaymentsStateModel>, { forceRefresh }: GetCredits): Observable<Credit[]> {
    const creditsStatus = this.store.selectSnapshot(PaymentsState.getCreditsStatus);
    if (!(LoadableHelper.shouldRequest(creditsStatus) || forceRefresh)) {
      return;
    }

    patchState({ credits: { status: LoadableStatus.LOADING } });

    const clientIds = this.authStateAdaptor.getClientIdsSnapshot();
    const creditLabelsStatus = this.store.selectSnapshot(PaymentsState.getCreditLabelsStatus);

    return (LoadableHelper.shouldRequest(creditLabelsStatus) ? dispatch(new GetCreditLabels()) : of(undefined)).pipe(
      switchMap(_ => {
        const creditLabels = this.store.selectSnapshot(PaymentsState.getCreditLabels);
        const creditLabelNames = creditLabels.map(({ name }) => name);
        return this.creditApiService.getCredits(clientIds, creditLabelNames);
      }),
      tap(result => this.trackCreditAllocationCheck(result)),
      map(result => {
        if (result.checkOutcome !== 'ok') {
          // Rethrow after tracking so the loadable lands in ERROR and stays retryable.
          // Caching LOADED with [] would latch an affirmative "no credits available" for the session.
          throw result.failure ?? new Error(`credit allocation check failed: ${result.checkOutcome}`);
        }
        return result.credits;
      }),
      // valueOnError [] so a failed check exposes an empty list, not undefined: the credit
      // selectors take Credit[] positionally and iterate it without a nullish guard.
      setLoadableInState(patchState, 'credits', []),
    );
  }

  // The credit fetch cross-checks the view against active allocation totals and fails
  // closed (see CreditApiService). Both outcomes are analytics events: SUPPRESSED is
  // the spent-credit-re-offered incident signature happening live; CHECK_FAILED means
  // every credit was withheld from the client, so they were asked to pay a balance their
  // credits should have covered. After tracking, the action rethrows a failed check so
  // the loadable errors instead of caching an empty credit list.
  private trackCreditAllocationCheck(result: CreditFetchResult): void {
    if (result.checkOutcome !== 'ok') {
      this.paymentTrackService.trackWorkflow(PaymentWorkflowEvent.PAYMENT_CREDIT_ALLOCATION_CHECK_FAILED, {
        ...(result.checkOutcome === 'lookup-error'
          ? extractFailureContext(result.failure)
          : { errorCategory: AIWYN_VALIDATION_ERROR_CATEGORY, errorMessage: 'credits missing from the allocation lookup' }),
        totalCreditCount: result.totalCreditCount,
        checkOutcome: result.checkOutcome,
      });
      return;
    }
    if (result.suppressedCredits.length) {
      this.paymentTrackService.trackWorkflow(PaymentWorkflowEvent.PAYMENT_CREDITS_SUPPRESSED, {
        suppressedCreditCount: result.suppressedCredits.length,
        totalCreditCount: result.totalCreditCount,
        suppressedTotal: result.suppressedCredits.reduce((sum, credit) => sum + Math.abs(Number(credit.amountRemaining)), 0),
        suppressedCreditIds: result.suppressedCredits.map(credit => credit.id).join(','),
      });
    }
  }

  @Action([SetPayWithInvoices])
  setPayWithInvoices({ patchState }: StateContext<PaymentsStateModel>, { payWithInvoices }: SetPayWithInvoices): void {
    patchState({ payWithInvoices });
  }

  @Action([GetJobs])
  getJobs({ patchState }: StateContext<PaymentsStateModel>, { forceRefresh }: GetJobs): Observable<BaseJob[]> | void {
    const jobsStatus = this.store.selectSnapshot(PaymentsState.getJobsStatus);
    if (!(LoadableHelper.shouldRequest(jobsStatus) || forceRefresh)) {
      return;
    }

    patchState({ jobs: { status: LoadableStatus.LOADING } });

    return this.jobApiService.getJobs().pipe(
      setLoadableInState(patchState, 'jobs'),
      take(1),
    );
  }

  @Action([GetClients])
  getClients({ patchState }: StateContext<PaymentsStateModel>, { forceRefresh }: GetClients): Observable<Client[]> | void {
    const clientsStatus = this.store.selectSnapshot(PaymentsState.getClientsStatus);
    if (!(LoadableHelper.shouldRequest(clientsStatus) || forceRefresh)) {
      return;
    }

    patchState({ clients: { status: LoadableStatus.LOADING } });

    return this.clientApiService.getClientsFromClientIdList().pipe(
      setLoadableInState(patchState, 'clients'),
      take(1),
    );
  }

  @Action([GetLateFees])
  getLateFees({ patchState }: StateContext<PaymentsStateModel>, { forceRefresh }: GetLateFees): Observable<unknown> {
    const lateFeesStatus = this.store.selectSnapshot(PaymentsState.getLateFeesStatus);
    if (!(LoadableHelper.shouldRequest(lateFeesStatus) || forceRefresh)) {
      return getLoadedResults(this.store.select(PaymentsState.getLateFeesLoadable), true).pipe(take(1));
    }

    patchState({ lateFees: { status: LoadableStatus.LOADING } });

    return this.authService.getUser().pipe(
      take(1),
      switchMap(user => this.feeApiService.getLateFees(user.getClientIdList())),
      tap(result => this.trackLateFeeAllocationCheck(result)),
      map(result => {
        if (result.checkOutcome !== 'ok') {
          // Rethrow after tracking so the loadable lands in ERROR and stays retryable.
          // Caching LOADED with [] would latch an affirmative "no fees owed" for the session.
          throw result.failure ?? new Error(`late fee allocation check failed: ${result.checkOutcome}`);
        }
        return result.fees;
      }),
      setLoadableInState(patchState, 'lateFees'),
      ignoreEmpty(),
      switchMap(_ => this.store.dispatch(new SetPayLateFees(true))),
    );
  }

  // The fee fetch cross-checks the view against active allocation totals and fails
  // closed (see FeeApiService). Both outcomes are analytics events: SUPPRESSED is
  // the paid-fee-re-offered incident signature happening live; CHECK_FAILED means
  // every fee was withheld from the client. After tracking, the action rethrows a
  // failed check so the loadable errors instead of caching an empty fee list.
  private trackLateFeeAllocationCheck(result: LateFeeFetchResult): void {
    if (result.checkOutcome !== 'ok') {
      this.paymentTrackService.trackWorkflow(PaymentWorkflowEvent.PAYMENT_LATE_FEE_ALLOCATION_CHECK_FAILED, {
        ...(result.checkOutcome === 'lookup-error'
          ? extractFailureContext(result.failure)
          : { errorCategory: AIWYN_VALIDATION_ERROR_CATEGORY, errorMessage: 'fees missing from the allocation lookup' }),
        totalFeeCount: result.totalFeeCount,
        checkOutcome: result.checkOutcome,
      });
      return;
    }
    if (result.suppressedFees.length) {
      this.paymentTrackService.trackWorkflow(PaymentWorkflowEvent.PAYMENT_LATE_FEES_SUPPRESSED, {
        suppressedFeeCount: result.suppressedFees.length,
        totalFeeCount: result.totalFeeCount,
        suppressedTotal: result.suppressedFees.reduce((sum, fee) => sum + Number(fee.amount), 0),
        suppressedFeeIds: result.suppressedFees.map(fee => fee.id).join(','),
      });
    }
  }

  @Action([GetPaymentFeeConfigs])
  getPaymentFeeConfigs(
    { patchState }: StateContext<PaymentsStateModel>,
    { forceRefresh }: GetPaymentFeeConfigs,
  ): Observable<PaymentFeeConfig[]> {
    const paymentFeeConfigsStatus = this.store.selectSnapshot(PaymentsState.getPaymentFeeConfigsStatus);
    if (!(LoadableHelper.shouldRequest(paymentFeeConfigsStatus) || forceRefresh)) {
      return getLoadedResults(this.store.select(PaymentsState.getPaymentFeeConfigsLoadable), true).pipe(take(1));
    }

    patchState({ paymentFeeConfigs: { status: LoadableStatus.LOADING } });

    return this.feeApiService.getPaymentFeeConfigs().pipe(
      setLoadableInState(patchState, 'paymentFeeConfigs'),
    );
  }

  @Action([UpdatePayWithoutInvoicePayable])
  updatePayWithoutInvoicePayable(
    { patchState }: StateContext<PaymentsStateModel>,
    { partialPayWithoutInvoicePayable }: UpdatePayWithoutInvoicePayable,
  ): void {
    const existingPayWithoutInvoicePayable = this.store.selectSnapshot(PaymentsState.getPayWithoutInvoicePayable);
    patchState({ payWithoutInvoicePayable: { ...existingPayWithoutInvoicePayable, ...partialPayWithoutInvoicePayable } });
  }

  @Action([SetPayLateFees])
  setPayLateFees({ patchState }: StateContext<PaymentsStateModel>, { payLateFees }: SetPayLateFees): void {
    patchState({ payAllLateFees: payLateFees });
  }

  @Action([AddPayLateFeeClientIds])
  addPayLateFeeClientIds({ patchState }: StateContext<PaymentsStateModel>, { clientIds }: AddPayLateFeeClientIds): void {
    const payLateFeeClientIds = this.store.selectSnapshot(PaymentsState.getPayLateFeeClientIds) ?? [];
    const newPayLateFeeClientIds = removeDuplicates(payLateFeeClientIds.concat(clientIds));
    patchState({ payLateFeeClientIds: newPayLateFeeClientIds });
  }

  @Action([RemovePayLateFeeClientIds])
  removePayLateFeeClientIds({ patchState }: StateContext<PaymentsStateModel>, { clientIds }: RemovePayLateFeeClientIds): void {
    const clientIdsToRemove = forceArray(clientIds);
    const payLateFeeClientIds = this.store.selectSnapshot(PaymentsState.getPayLateFeeClientIds);
    const newPayLateFeeClientIds = payLateFeeClientIds.filter(clientId => !clientIdsToRemove.includes(clientId));
    patchState({ payLateFeeClientIds: isEmpty(newPayLateFeeClientIds) ? undefined : newPayLateFeeClientIds });
  }

  @Action([RemoveInapplicablePayLateFeeClientIds])
  removeInapplicablePayLateFeeClientIds({ dispatch }: StateContext<PaymentsStateModel>): void | Observable<void> {
    const payLateFeeClientIds = this.store.selectSnapshot(PaymentsState.getPayLateFeeClientIds);
    if (isEmpty(payLateFeeClientIds)) {
      return;
    }

    const arTranAllocations = this.store.selectSnapshot(PaymentsState.getArTranAllocationsWithClientIds);
    const arTranAllocationClientIds = removeDuplicates(arTranAllocations.map(({ clientId }) => clientId));
    const clientIdsWithNoArTranAllocations = payLateFeeClientIds.filter(clientId => !arTranAllocationClientIds.includes(clientId));

    return dispatch(new RemovePayLateFeeClientIds(clientIdsWithNoArTranAllocations));
  }

  @Action([ClearPayLateFeeClientIds])
  clearPayLateFeeClientIds({ patchState }: StateContext<PaymentsStateModel>): void {
    patchState({ payLateFeeClientIds: undefined });
  }

  @Action([SetShouldSaveNewPaymentMethod])
  setShouldSaveNewPaymentMethod(
    { patchState }: StateContext<PaymentsStateModel>,
    { shouldSaveNewPaymentMethod }: SetShouldSaveNewPaymentMethod,
  ): void {
    patchState({ shouldSaveNewPaymentMethod });
  }

  @Action([SetShouldSetNewPaymentMethodAsDefault])
  setShouldSetNewPaymentMethodAsDefault(
    { patchState }: StateContext<PaymentsStateModel>,
    { shouldSetNewPaymentMethodAsDefault }: SetShouldSetNewPaymentMethodAsDefault,
  ): void {
    patchState({ shouldSetNewPaymentMethodAsDefault });
  }

  @Action([ClearNewPaymentMethodData])
  clearNewPaymentMethodData({ patchState }: StateContext<PaymentsStateModel>): void {
    patchState({ shouldSaveNewPaymentMethod: undefined, shouldSetNewPaymentMethodAsDefault: undefined });
  }

  @Action([SetProvidedPrimaryRecipientEmail])
  setPrimaryRecipientEmail(
    { patchState }: StateContext<PaymentsStateModel>,
    { providedPrimaryRecipientEmail }: SetProvidedPrimaryRecipientEmail,
  ): void {
    patchState({ providedPrimaryRecipientEmail });
  }

  @Action([SetPaymentEntrySource])
  setPaymentEntrySource(
    { patchState }: StateContext<PaymentsStateModel>,
    { paymentEntrySource }: SetPaymentEntrySource,
  ): void {
    patchState({ paymentEntrySource });
  }

  @Action([SetAdditionalRecipientEmail])
  setAdditionalRecipientEmail(
    { patchState }: StateContext<PaymentsStateModel>,
    { additionalRecipientEmails }: SetAdditionalRecipientEmail,
  ): void {
    patchState({ additionalRecipientEmails });
  }

  @Action([GetQuickPaymentLinkId])
  getQuickPaymentLinkId({ patchState }: StateContext<PaymentsStateModel>, { quickPaymentLink }: GetQuickPaymentLinkId): Observable<number> {
    return this.paymentsApiService.openPaymentLink(quickPaymentLink).pipe(
      tap(result => patchState({ quickPaymentLinkArgs: result })),
      map(result => result.id),
      setLoadableInState(patchState, 'quickPaymentLinkId'),
    );
  }

  @Action([ClearQuickPaymentLinkId])
  clearQuickPaymentLinkId({ patchState }: StateContext<PaymentsStateModel>): void {
    patchState({ quickPaymentLinkId: DEFAULT_LOADABLE_STATE, quickPaymentLinkArgs: undefined });
  }

  @Action([ApplyPaymentToArTranAllocations])
  applyPaymentToArTranAllocations({ patchState }: StateContext<PaymentsStateModel>): void {
    const selectedPayables = this.store.selectSnapshot(PaymentsState.getSelectedPayables);
    const arTranAllocations = this.store.selectSnapshot(PaymentsState.getArTranAllocations);
    const remainingArTranAllocations = arTranAllocations.reduce(
      (acc, arTranAllocation) => {
        const matchingPayable = selectedPayables.find(({ arTranId }) => arTranId === arTranAllocation.arTranId);
        if (matchingPayable == undefined) {
          return acc;
        }

        const newAmountRemaining = matchingPayable.amountRemaining - arTranAllocation.allocatedAmount;

        return newAmountRemaining === 0
          ? acc
          : acc.concat({ ...arTranAllocation, allocatedAmount: newAmountRemaining });
      },
      new Array<ArTranAllocation>(),
    );
    patchState({ arTranAllocations: remainingArTranAllocations.length === 0 ? undefined : remainingArTranAllocations });
  }

  @Action([SetArTranAllocations])
  setArTranAllocations(
    { patchState }: StateContext<PaymentsStateModel>,
    { arTranAllocations }: SetArTranAllocations,
  ): void | Observable<unknown> {
    arTranAllocations = forceArray(arTranAllocations);

    // we have to toggle the state to ensure that the UI updates correctly
    patchState({ arTranAllocations: undefined });
    patchState({ arTranAllocations });

    return this._onClientAddedToOrRemovedFromPayment(arTranAllocations.map(({ arTranId }) => arTranId), false);
  }

  @Action([UpdateArTranAllocations])
  updateArTranAllocations(
    { patchState }: StateContext<PaymentsStateModel>,
    { arTranAllocations, intentTrigger }: UpdateArTranAllocations,
  ): void | Observable<unknown> {
    arTranAllocations = forceArray(arTranAllocations);

    const incomingArTranIds = arTranAllocations.map(({ arTranId }) => arTranId);
    const existingArTranAllocations = this.store.selectSnapshot(PaymentsState.getArTranAllocations) ?? [];
    // add to or replace existing arTranAllocations with the incoming ones
    const updatedArTranAllocations = existingArTranAllocations
      .filter(({ arTranId }) => !incomingArTranIds.includes(arTranId))
      .concat(arTranAllocations);

    patchState({ arTranAllocations: updatedArTranAllocations });

    // track workflow event (don't fire if none are selected)
    if (!isEmpty(updatedArTranAllocations)) {
      const subtotal = this.store.selectSnapshot(PaymentsState.getArTranAllocationsTotal);
      this.paymentTrackService.trackWorkflow(
        PaymentWorkflowEvent.PAYMENT_PAYABLES_SELECTED,
        {
          ...PaymentsState.buildPaymentEventBase(this.store),
          subtotal,
          payableCount: updatedArTranAllocations.length,
          intentTrigger,
        },
      );
    }

    const addPayLateFeeClientIdsAction = this._getAddPayLateFeeClientIdsAction(incomingArTranIds);
    const addCreditAppliedClientIdsAction = this._getAddCreditAppliedClientIdsAction(incomingArTranIds);
    const actions = [...addPayLateFeeClientIdsAction, ...addCreditAppliedClientIdsAction];
    return isEmpty(actions) ? undefined : this.store.dispatch(actions);
  }

  @Action([RemoveArTranAllocations])
  removeArTranAllocations(
    { patchState }: StateContext<PaymentsStateModel>,
    { arTranIds }: RemoveArTranAllocations,
  ): void | Observable<unknown> {
    const existingArTranAllocations = this.store.selectSnapshot(PaymentsState.getArTranAllocations);
    const updatedArTranAllocations = existingArTranAllocations.filter(
      Array.isArray(arTranIds)
        ? ({ arTranId }) => !arTranIds.includes(arTranId)
        : ({ arTranId }) => arTranId !== arTranIds,
    );

    patchState({ arTranAllocations: updatedArTranAllocations.length === 0 ? undefined : updatedArTranAllocations });

    return this._onClientAddedToOrRemovedFromPayment(forceArray(arTranIds), true);
  }

  @Action([ClearArTranAllocations])
  clearArTranAllocations({ patchState, dispatch }: StateContext<PaymentsStateModel>): void | Observable<void> {
    patchState({ arTranAllocations: undefined });

    const creditAppliedClientIds = this.store.selectSnapshot(PaymentsState.getCreditAppliedClientIds);
    const payLateFeeClientIds = this.store.selectSnapshot(PaymentsState.getPayLateFeeClientIds);
    const actions = []
      .concat(isEmpty(creditAppliedClientIds) ? [] : new ClearCreditAppliedClientIds())
      .concat(isEmpty(payLateFeeClientIds) ? [] : new ClearPayLateFeeClientIds());

    return isEmpty(actions) ? undefined : dispatch(actions);
  }

  /**
   * Whenever a client is potentially added to or removed from the payment, ensure credits and late fees are updated accordingly.
   * @param arTranIds
   * @param isRemovingSpecifiedArTranIds
   * @private
   */
  private _onClientAddedToOrRemovedFromPayment(arTranIds: number[], isRemovingSpecifiedArTranIds: boolean): void | Observable<unknown> {
    // late fee data prep
    const payLateFeeClientIds = this.store.selectSnapshot(PaymentsState.getPayLateFeeClientIds);
    const isPayLateFeesByClientEnabled = this.store.selectSnapshot(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_APPLY_CREDITS_TO_LATE_FEES));
    const shouldRemovePayLateFeeClientIds = (!this.configService.paymentLateFeeRequired || true) // TODO: Get backend to allow for paying late fees without payables
      && isPayLateFeesByClientEnabled
      && !isEmpty(payLateFeeClientIds);

    // credit data prep
    const creditAppliedClientIds = this.store.selectSnapshot(PaymentsState.getCreditAppliedClientIds);

    const actions = []
      .concat(isEmpty(creditAppliedClientIds) ? [] : new RemoveInapplicableCreditAppliedClientIds())
      .concat(shouldRemovePayLateFeeClientIds ? new RemoveInapplicablePayLateFeeClientIds() : [])
      // paying late fees defaults to true, so when a new client is added to the sidebar, we need to ensure their ID is added
      .concat(isRemovingSpecifiedArTranIds ? [] : this._getAddPayLateFeeClientIdsAction(arTranIds))
      .concat(isRemovingSpecifiedArTranIds ? [] : this._getAddCreditAppliedClientIdsAction(arTranIds));

    return isEmpty(actions) ? undefined : this.store.dispatch(actions);
  }

  private _getAddPayLateFeeClientIdsAction(arTranIds: number[]): [AddPayLateFeeClientIds] | [] {
    if (!this.store.selectSnapshot(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_APPLY_CREDITS_TO_LATE_FEES))) {
      return [];
    }

    const newPayLateFeeClientIds = this._getNewPayLateFeeClientIds(arTranIds);
    return isEmpty(newPayLateFeeClientIds) ? [] : [new AddPayLateFeeClientIds(newPayLateFeeClientIds)];
  }

  private _getNewPayLateFeeClientIds(arTranIds: number[]): number[] {
    const lateFeeClientIds = this.store.selectSnapshot(PaymentsState.getLateFees).map(({ clientId }) => clientId);

    /** only add clients who have selected payables */
    if (!this.configService.paymentLateFeeRequired || true) { // TODO: Get backend to allow for paying late fees without payables
      const payables = this.store.selectSnapshot(PaymentsState.getPayables) ?? [];
      const arTranAllocationClientIds = removeDuplicates(
        arTranIds.map(arTranId => payables.find(payable => payable.arTranId === arTranId).invoice.arTran.clientId),
      );
      const existingPayLateFeeClientIds = this.store.selectSnapshot(PaymentsState.getPayLateFeeClientIds);
      return arTranAllocationClientIds.filter(clientId =>
        lateFeeClientIds.includes(clientId) && !existingPayLateFeeClientIds.includes(clientId),
      );
    }

    /** add every client that is part of the same business entity */
    const selectedBusinessEntity = this.store.selectSnapshot(PaymentsState.getSelectedBusinessEntity(true));
    const defaultBusinessEntityId = this.store.selectSnapshot(ConfigState.getDefaultBusinessEntity).id;
    const clients = this.store.selectSnapshot(PaymentsState.getClients);
    return lateFeeClientIds.filter(clientId => {
      const client = clients.find(c => c.id === clientId);
      return (client.businessEntityId ?? defaultBusinessEntityId) === selectedBusinessEntity.id;
    });
  }

  private _getAddCreditAppliedClientIdsAction(arTranIds: number[]): [AddCreditAppliedClientIds] | [] {
    if (!this.configService.creditsEnabled || !this.configService.enableClientPortalCreditAutoselection) {
      return [];
    }

    const newCreditAppliedClientIds = this._getNewCreditAppliedClientIds(arTranIds);
    return isEmpty(newCreditAppliedClientIds) ? [] : [new AddCreditAppliedClientIds(newCreditAppliedClientIds)];
  }

  private _getNewCreditAppliedClientIds(arTranIds: number[]): number[] {
    const creditClientIds = (this.store.selectSnapshot(PaymentsState.getCredits) ?? []).map(({ clientId }) => clientId);
    const payables = this.store.selectSnapshot(PaymentsState.getPayables) ?? [];
    const arTranAllocationClientIds = removeDuplicates(
      arTranIds.map(arTranId => payables.find(payable => payable.arTranId === arTranId).invoice.arTran.clientId),
    );
    const existingCreditAppliedClientIds = this.store.selectSnapshot(PaymentsState.getCreditAppliedClientIds) ?? [];
    return arTranAllocationClientIds.filter(clientId =>
      creditClientIds.includes(clientId) && !existingCreditAppliedClientIds.includes(clientId),
    );
  }

  @Action([ClearCreditAppliedClientIds])
  clearCreditAllocations({ patchState }: StateContext<PaymentsStateModel>): void {
    patchState({ creditAppliedClientIds: undefined });
  }

  @Action([SetCustomProcessingFeeRate])
  setCustomProcessingFee({ patchState }: StateContext<PaymentsStateModel>, { customProcessingFeeRate }: SetCustomProcessingFeeRate): void {
    patchState({ customProcessingFeeRate });
  }

  @Action([FillPartialArTranAllocations])
  fillExistingArTranAllocations({ patchState }: StateContext<PaymentsStateModel>): void {
    const existingArTranAllocations = this.store.selectSnapshot(PaymentsState.getArTranAllocations);
    if (existingArTranAllocations == undefined || existingArTranAllocations.length === 0) {
      return;
    }

    const selectedPayables = this.store.selectSnapshot(PaymentsState.getSelectedPayables);
    const fullArTranAllocations = existingArTranAllocations.map(arTranAllocation => ({
      ...arTranAllocation,
      allocatedAmount: selectedPayables.find(({ arTranId }) => arTranId === arTranAllocation.arTranId).amountRemaining,
    }));

    patchState({ arTranAllocations: fullArTranAllocations });
  }

  @Action([AddCreditAppliedClientIds])
  addCreditAppliedClientIds({ patchState }: StateContext<PaymentsStateModel>, { clientIds }: AddCreditAppliedClientIds): void {
    const creditAppliedClientIds = this.store.selectSnapshot(PaymentsState.getCreditAppliedClientIds) ?? [];
    const newCreditAppliedClientIds = removeDuplicates(creditAppliedClientIds.concat(clientIds));
    patchState({ creditAppliedClientIds: newCreditAppliedClientIds });
  }

  @Action([RemoveCreditAppliedClientIds])
  removeCreditAppliedClientId({ patchState }: StateContext<PaymentsStateModel>, { clientIds }: RemoveCreditAppliedClientIds): void {
    const clientIdsToRemove = Array.isArray(clientIds) ? clientIds : [clientIds];
    const creditAppliedClientIds = this.store.selectSnapshot(PaymentsState.getCreditAppliedClientIds);
    const newCreditAppliedClientIds = creditAppliedClientIds.filter(clientId => !clientIdsToRemove.includes(clientId));
    patchState({ creditAppliedClientIds: isEmpty(newCreditAppliedClientIds) ? undefined : newCreditAppliedClientIds });
  }

  @Action([RemoveInapplicableCreditAppliedClientIds])
  removeInapplicableCreditAllocations({ dispatch }: StateContext<PaymentsStateModel>): Observable<void> {
    const creditAppliedClientIds = this.store.selectSnapshot(PaymentsState.getCreditAppliedClientIds);
    if (creditAppliedClientIds.length === 0) {
      return;
    }

    const arTranAllocations = this.store.selectSnapshot(PaymentsState.getArTranAllocationsWithClientIds);
    const arTranAllocationClientIds = removeDuplicates(arTranAllocations.map(({ clientId }) => clientId));
    const clientIdsWithNoArTranAllocations = creditAppliedClientIds.filter(clientId => !arTranAllocationClientIds.includes(clientId));

    return dispatch(new RemoveCreditAppliedClientIds(clientIdsWithNoArTranAllocations));
  }

  getChargePayloads(
    selectedPaymentMethod: AbstractPaymentMethod,
    additionalPaymentData: PaymentPlatformPaymentData,
    isAdmin: boolean,
    doCreditsCoverArTranAllocations: boolean = false,
    paymentLinkId?: number,
  ): ChargePayload[] {
    /**
     * base charge payload
     */
    const selectedBusinessEntityId = this.store.selectSnapshot(PaymentsState.getSelectedBusinessEntity(true)).id;
    const sharedBaseChargePayload: SharedBaseChargePayload = {
      contactId: this.authAdaptor.getContactIdOrNull,
      businessEntityId: selectedBusinessEntityId,
    };

    /**
     * base charge details
     */
    const allRecipientEmails = this.getAllRecipientEmailsSnapshot();
    const sharedBaseChargeDetails: SharedBaseChargeDetails = {
      setupIntentId: additionalPaymentData.setupIntentId,
      paymentMethodId: selectedPaymentMethod.id,
      notifyToList: allRecipientEmails,
    };

    /**
     * base payment details
     */
    const processingFeeOverridden = this.store.selectSnapshot(PaymentsState.getProcessingFeeRateOverridden);
    const staffId = this.authAdaptor.getStaffId;
    const contactId = this.authAdaptor.getContactIdOrNull;

    const payWithoutInvoice = !this.store.selectSnapshot(PaymentsState.getPayWithInvoices);
    const canPayLateFees = this.store.selectSnapshot(PaymentsState.getCanPayLateFees);
    const lateFeeIds = canPayLateFees ? this.store.selectSnapshot(PaymentsState.getRemainingSelectedLateFees).map(({ id }) => id) : [];

    const sharedBasePaymentDetails: SharedBasePaymentDetails = {
      // TODO: These will be removed eventually. It all happens on the backend.
      applicationFeeFixedAmount: 0,
      applicationFeeCapAmount: 0,

      businessEntityId: selectedBusinessEntityId,
      lateFeeIds,
      clientIdList: [],
      ...(isAdmin ? { processingFeeOverridden, staffId } : { contactId }),
    };

    /**
     * generic dependencies
     */
    const sharedBaseChargePayloadParts: SharedBaseChargePayloadParts = {
      chargePayload: sharedBaseChargePayload,
      chargeDetails: sharedBaseChargeDetails,
      paymentDetails: sharedBasePaymentDetails,
      paymentLinkId,
    };

    if (payWithoutInvoice) {
      const payWithoutInvoicePayable = this.store.selectSnapshot(PaymentsState.getPayWithoutInvoicePayable) as PayWithoutInvoicePayable;
      const payWithoutInvoiceChargePayload = this._getWithoutInvoicesChargePayload(
        sharedBaseChargePayloadParts,
        selectedPaymentMethod,
        payWithoutInvoicePayable,
        isAdmin,
      );

      return [payWithoutInvoiceChargePayload];
    }

    const payablesChargePayload = doCreditsCoverArTranAllocations
      ? undefined
      : this._getInvoicesChargePayload(sharedBaseChargePayloadParts, selectedPaymentMethod, isAdmin) as ChargePayload;

    const addOns = this.store.selectSnapshot(PaymentsState.getAddOns);
    if (addOns.length === 0) {
      if (doCreditsCoverArTranAllocations) {
        const errorMessage = 'Should not be getting charge payloads when credits cover AR Tran allocations and there are no add-ons.';
        this.logger.error(`PAYMENT : PaymentsState : getChargePayloads : ${errorMessage}`);
        throw new Error(errorMessage);
      }

      return [payablesChargePayload];
    }

    const addOnOptions = this.store.selectSnapshot(PaymentsState.getAddOnOptions);
    const addOnPayables: PayWithoutInvoicePayable[] = addOns.map(addOn => {
      const typeId = addOnOptions.find(({ type }) => type === addOn.type).id;

      return {
        clientId: addOn.account.id,
        subtotal: +addOn.paymentAmount,
        typeId,
        notes: addOn.notes,
      };
    });
    const addOnChargePayloads = addOnPayables.map(addOnPayable =>
      this._getWithoutInvoicesChargePayload(sharedBaseChargePayloadParts, selectedPaymentMethod, addOnPayable, isAdmin, true),
    );

    return [payablesChargePayload].concat(addOnChargePayloads);
  }

  private _getInvoicesChargePayload(
    { chargePayload, chargeDetails, paymentDetails, paymentLinkId }: SharedBaseChargePayloadParts,
    selectedPaymentMethod: AbstractPaymentMethod,
    isAdmin: boolean,
  ): InvoiceChargePayload {
    const fullSubtotal = this.store.selectSnapshot(PaymentsState.getSubtotal);
    const addOnsSubtotal = this.store.selectSnapshot(PaymentsState.getAddOnsSubtotal);
    const subtotal = fullSubtotal - addOnsSubtotal;

    const getTotalOptions: GetTotalOptions = {
      subtotal: subtotal,
      currencyCode: this.store.selectSnapshot(PaymentsState.getActiveCurrency).code,
    };

    const payablesTotal = this.store.selectSnapshot(PaymentsState.getTotal(selectedPaymentMethod, getTotalOptions));
    const internationalFeeAmount = this.store.selectSnapshot(PaymentsState.getInternationalFee(selectedPaymentMethod, getTotalOptions));

    const payablesSubtotal = this.store.selectSnapshot(PaymentsState.getSelectedPayablesSubtotal);
    const bundledLateFeesSubtotal = this.store.selectSnapshot(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_APPLY_CREDITS_TO_LATE_FEES))
      ? this.store.selectSnapshot(PaymentsState.getSelectedLateFeesTotal)
      : 0;
    const processingFee = this.store.selectSnapshot(
      PaymentsState.getProcessingFee(selectedPaymentMethod, { ...getTotalOptions, subtotal: payablesSubtotal + bundledLateFeesSubtotal }),
    );

    let arTranAllocations = this.store.selectSnapshot(PaymentsState.getArTranAllocations).map(arTranAllocation => ({
      ...arTranAllocation,
      allocatedAmount: roundMonetaryValue(arTranAllocation.allocatedAmount),
    })) as NonEmptyArray<ArTranAllocation>;

    const creditAllocations: CreditAllocation[] = (this.store.selectSnapshot(PaymentsState.getCreditAllocations) ?? [])
      .map(creditAllocation => ({ ...creditAllocation, allocated: roundMonetaryValue(creditAllocation.allocated) }));

    if (!isEmpty(creditAllocations)) {
      // subtract Credit allocations from AR Tran allocations (will represent what is left to pay after applying credits)
      arTranAllocations = creditAllocations.reduce(
        (acc, creditAllocation) => {
          const matchingArTranAllocation = acc.find(({ arTranId }) => arTranId === creditAllocation.arTranId);
          if (matchingArTranAllocation == undefined) {
            return acc; // found a credit allocation to a late fee
          }

          const amountRemaining = matchingArTranAllocation.allocatedAmount + creditAllocation.allocated; // add because credit is negative
          if (amountRemaining === 0) {
            return removeEntityByEntry(acc, 'arTranId', matchingArTranAllocation.arTranId);
          }

          const matchingArTranAllocationIndex = acc.indexOf(matchingArTranAllocation);
          return replaceArrayItem(
            acc,
            matchingArTranAllocationIndex,
            { ...matchingArTranAllocation, allocatedAmount: roundMonetaryValue(amountRemaining) },
          );
        },
        arTranAllocations,
      ) as NonEmptyArray<ArTranAllocation>;
    }

    const adminCanEditProcessingFee = this.store.selectSnapshot(ConfigState.getAdminCanEditCcProcessingFee);
    const idempotencyKey = PaymentsState.getInvoicesIdempotencyKey(
      this.globals.getTenant(),
      selectedPaymentMethod,
      payablesTotal,
      arTranAllocations,
    );

    // TODO: not sure why client ID is pulled from this, especially since a payment can have multiple clients
    const selectedPayables = this.store.selectSnapshot(PaymentsState.getSelectedPayables);

    return {
      ...chargePayload,
      paymentLinkId,
      idempotencyKey,
      chargeDetails: {
        ...chargeDetails,
        paymentDetails: {
          ...paymentDetails,
          amount: payablesTotal,
          clientId: selectedPayables[0].invoice.arTran.clientId,
          internationalFeeAmount: internationalFeeAmount || undefined, // don't pass zero
          overriddenProcessingFeeAmount: isAdmin && adminCanEditProcessingFee ? processingFee : undefined,
          payorName: selectedPaymentMethod?.billingDetails?.name,
        },
        arTranAllocations,
        creditAllocations,
      },
    };
  }

  private _getWithoutInvoicesChargePayload(
    { chargePayload, chargeDetails, paymentDetails, paymentLinkId }: SharedBaseChargePayloadParts,
    selectedPaymentMethod: AbstractPaymentMethod,
    payWithoutInvoicePayable: PayWithoutInvoicePayable | PayOpenPortalPayable,
    isAdmin: boolean,
    isAddOn = false,
  ): WithoutInvoiceChargePayload {
    const getTotalOptions: GetTotalOptions = {
      subtotal: payWithoutInvoicePayable.subtotal,
      ignoreLateFees: isAddOn,
      currencyCode: this.store.selectSnapshot(PaymentsState.getActiveCurrency).code,
    };

    const processingFee = this.store.selectSnapshot(PaymentsState.getProcessingFee(selectedPaymentMethod, getTotalOptions));
    const internationalFee = this.store.selectSnapshot(PaymentsState.getInternationalFee(selectedPaymentMethod, getTotalOptions));
    const payWithoutInvoicePayableTotal = this.store.selectSnapshot(PaymentsState.getTotal(selectedPaymentMethod, getTotalOptions));

    const adminCanEditProcessingFee = this.store.selectSnapshot(ConfigState.getAdminCanEditCcProcessingFee);
    const idempotencyKey = PaymentsState.getWithoutInvoicesIdempotencyKey(
      this.globals.getTenant(),
      selectedPaymentMethod,
      payWithoutInvoicePayableTotal,
      payWithoutInvoicePayable,
    );

    return {
      ...chargePayload,
      paymentLinkId,
      idempotencyKey,
      chargeDetails: {
        ...chargeDetails,
        paymentDetails: {
          ...paymentDetails,
          amount: payWithoutInvoicePayableTotal,
          clientId: payWithoutInvoicePayable.clientId,
          invoiceAddOnTypeId: payWithoutInvoicePayable.typeId,
          // validation has already occurred for no add on options, so this is a safe fallback (cannot occur for add-ons)
          notes: payWithoutInvoicePayable.notes ?? 'Pay without invoice',
          internationalFeeAmount: internationalFee || undefined, // don't pass zero
          overriddenProcessingFeeAmount: isAdmin && adminCanEditProcessingFee ? processingFee : undefined,
          ...('companyName' in payWithoutInvoicePayable ? { companyName: payWithoutInvoicePayable.companyName } : {}),
          ...(isAdmin ? { jobId: payWithoutInvoicePayable.jobId } : {}),
          ...(isAddOn ? { lateFeeIds: [] } : {}),
          payorName: selectedPaymentMethod?.billingDetails?.name,
        },
        arTranAllocations: [],
      },
    } as WithoutInvoiceChargePayload; // doesn't seem to understand the `...paymentDetails` part
  }

  //#region payment event tracking
  // Per-event helpers — pulled out of `makePayment` so the action body reads as orchestration, not metadata gathering.
  // chargeIndex: 0 = initial payables/pay-without-invoice charge; 1..N = the Nth add-on charge.

  /**
   * Compute the charge-level financial fields shared by PAYMENT_CHARGE_SUBMITTED and (on charge-submit
   * failures) PAYMENT_CHARGE_FAILED. Selectors are read against the live store, so callers must invoke
   * this before any state mutations that would change subtotal / fee inputs — both fire sites today are
   * pre-mutation (just before dispatch, and inside the `makePayment` catchError) so this holds.
   */
  private _computeChargeFinancials(
    selectedPaymentMethod: AbstractPaymentMethod,
    chargePayload: ChargePayload,
    chargeIndex: number,
  ): {
    amount: number;
    subtotal: number;
    processingFeeAmount: number;
    internationalFeeAmount: number;
    creditAmount?: number;
    lateFeeAmount?: number;
  } {
    const paymentDetails = chargePayload?.chargeDetails?.paymentDetails;
    const amount = paymentDetails?.amount ?? 0;
    // `internationalFeeAmount` is set to `undefined` on the payload when zero (see "don't pass zero" in _getInvoicesChargePayload).
    const internationalFeeAmount = paymentDetails?.internationalFeeAmount ?? 0;
    const currencyCode = this.store.selectSnapshot(PaymentsState.getActiveCurrency).code;

    if (chargeIndex > 0) {
      // Add-on charge — its source subtotal lives in getAddOns at index (chargeIndex - 1). Processing fee re-selected with the
      // exact options _getWithoutInvoicesChargePayload used at construction, so values match the constructed payload.
      const addOn = this.store.selectSnapshot(PaymentsState.getAddOns)[chargeIndex - 1];
      const subtotal = +(addOn?.paymentAmount ?? 0);
      const feeOptions: GetTotalOptions = { subtotal, ignoreLateFees: true, currencyCode };
      const processingFeeAmount = this.store.selectSnapshot(PaymentsState.getProcessingFee(selectedPaymentMethod, feeOptions)) ?? 0;

      return { amount, subtotal, processingFeeAmount, internationalFeeAmount };
    }

    // Initial charge — payables / pay-without-invoice flow. Carries credit + late-fee deductions that don't apply to add-ons.
    const payWithInvoices = this.store.selectSnapshot(PaymentsState.getPayWithInvoices);
    const addOnsSubtotal = this.store.selectSnapshot(PaymentsState.getAddOnsSubtotal) ?? 0;
    const subtotal = payWithInvoices
      ? (this.store.selectSnapshot(PaymentsState.getSubtotal) ?? 0) - addOnsSubtotal
      : this.store.selectSnapshot(PaymentsState.getPayWithoutInvoicePayable)?.subtotal ?? 0;
    const creditAmount = Math.abs(this.store.selectSnapshot(PaymentsState.getAppliedCreditTotal) ?? 0);
    const lateFeeAmount = this.store.selectSnapshot(PaymentsState.getSelectedLateFeesTotal) ?? 0;
    const feeOptions: GetTotalOptions = { subtotal, currencyCode };
    const processingFeeAmount = this.store.selectSnapshot(PaymentsState.getProcessingFee(selectedPaymentMethod, feeOptions)) ?? 0;

    return { amount, subtotal, processingFeeAmount, internationalFeeAmount, creditAmount, lateFeeAmount };
  }

  private _trackPaymentChargeSubmitted(
    selectedPaymentMethod: AbstractPaymentMethod,
    chargePayload: ChargePayload,
    chargeIndex: number,
  ): void {
    this.paymentTrackService.trackWorkflow(PaymentWorkflowEvent.PAYMENT_CHARGE_SUBMITTED, {
      ...PaymentsState.buildPaymentEventBase(this.store, selectedPaymentMethod),
      chargeIndex,
      ...this._computeChargeFinancials(selectedPaymentMethod, chargePayload, chargeIndex),
      // ACH only: connected-account (Financial Connections) vs manual entry. Omitted for card /
      // ACSS / BACS, where the getter returns undefined.
      ...(selectedPaymentMethod.isConnectedAccount !== undefined
        ? { isConnectedAccount: selectedPaymentMethod.isConnectedAccount }
        : {}),
    });
    this.paymentTrackService.startOperation(PaymentsState._chargeOperationKey(chargeIndex));
  }

  private _trackPaymentChargeSucceeded(selectedPaymentMethod: AbstractPaymentMethod, amount: number, chargeIndex: number): void {
    this.paymentTrackService.trackWorkflow(PaymentWorkflowEvent.PAYMENT_CHARGE_SUCCEEDED, {
      ...PaymentsState.buildPaymentEventBase(this.store, selectedPaymentMethod),
      amount,
      chargeIndex,
      operationDurationMs: this.paymentTrackService.endOperation(PaymentsState._chargeOperationKey(chargeIndex)),
    });
  }

  private _trackChargeFailed(
    selectedPaymentMethod: AbstractPaymentMethod,
    chargePayload: ChargePayload,
    marshaledError: ChargePaymentError,
    originalError: unknown,
    chargeIndex: number,
  ): void {
    const failureContext = extractFailureContext(originalError);
    // Override errorCode with the marshaled HTTP status when present so dashboards keep the
    // legacy numeric-status filter; fall back to whatever extractFailureContext deduced.
    const errorCode = marshaledError.status != undefined ? String(marshaledError.status) : failureContext.errorCode;
    // The marshaledError carries the user-facing message ("payment declined", etc.); prefer it
    // over the raw exception message but pass through the sanitizer indirectly via FailureContext.
    const errorMessage = marshaledError.message ?? failureContext.errorMessage;
    this.paymentTrackService.trackWorkflow(PaymentWorkflowEvent.PAYMENT_CHARGE_FAILED, {
      ...PaymentsState.buildPaymentEventBase(this.store, selectedPaymentMethod),
      ...failureContext,
      errorCode,
      errorMessage,
      chargeIndex,
      operationDurationMs: this.paymentTrackService.endOperation(PaymentsState._chargeOperationKey(chargeIndex)),
      stripeDeclineCode: marshaledError.declineCode,
      stripeErrorCode: marshaledError.stripeErrorCode,
      ...this._computeChargeFinancials(selectedPaymentMethod, chargePayload, chargeIndex),
    });
  }

  private static _chargeOperationKey(chargeIndex: number): string {
    return `PAYMENT_CHARGE:${chargeIndex}`;
  }
  //#endregion

  private _assertMakePaymentPreconditions(selectedPaymentMethod: AbstractPaymentMethod | undefined): void {
    if (selectedPaymentMethod == undefined) {
      throw new Error('No payment method selected');
    }

    const payWithInvoices = this.store.selectSnapshot(PaymentsState.getPayWithInvoices);
    const arTranAllocations = this.store.selectSnapshot(PaymentsState.getArTranAllocations);
    const payWithoutInvoicePayable = this.store.selectSnapshot(PaymentsState.getPayWithoutInvoicePayable);

    if (payWithInvoices && (arTranAllocations ?? []).length === 0) {
      throw new Error('No AR transaction allocations');
    }

    if (payWithInvoices && this.store.selectSnapshot(PaymentsState.hasInvalidArTranAllocations)) {
      throw new Error(INVALID_AR_TRAN_ALLOCATION_ERROR);
    }

    const addOnOptions = this.store.selectSnapshot(PaymentsState.getAddOnOptions);
    const requiredFields = (['clientId', 'subtotal'] as (keyof PayWithoutInvoicePayable)[])
      .concat((addOnOptions ?? []).length > 0 ? ['typeId', 'notes'] : []);
    if (
      !payWithInvoices
      && (payWithoutInvoicePayable == undefined || requiredFields.some(field => payWithoutInvoicePayable[field] == undefined))
    ) {
      throw new Error('Missing required fields for pay without invoice payable');
    }
  }

  private _initExecutedPaymentBatch(
    patchState: StateContext<PaymentsStateModel>['patchState'],
    chargePayloads: ChargePayload[],
  ): number {
    const existingExecutedPayments = this.store.selectSnapshot(PaymentsState.getExecutedPaymentBatches);
    const newExecutedPaymentId = (this.store.selectSnapshot(PaymentsState.getLastExecutedPaymentBatchId) ?? -1) + 1;

    patchState({
      executedPaymentBatches: {
        ...existingExecutedPayments,
        [newExecutedPaymentId]: {
          initial: { status: LoadableStatus.LOADING, data: chargePayloads[0] },
          ...(
            chargePayloads.length > 1
              ? { addOns: chargePayloads.slice(1).map(chargePayload => ({ status: LoadableStatus.DEFAULT, data: chargePayload })) }
              : {}
          ),
        },
      },
    });

    return newExecutedPaymentId;
  }

  private _executeChargePayload(
    patchState: StateContext<PaymentsStateModel>['patchState'],
    chargePayload: ChargePayload,
    index: number,
    selectedPaymentMethod: AbstractPaymentMethod,
    newExecutedPaymentId: number,
    skipInitialPayment: boolean,
  ): Observable<PaymentActionRequirement | void> {
    const chargeAmount = chargePayload?.chargeDetails?.paymentDetails?.amount ?? 0;
    // chargeIndex is the position in the unfiltered chargePayloads array — 0 for the initial payables/pay-without-invoice charge,
    // 1..N for the Nth add-on charge. When initial is skipped (credits cover payables), filtered index 0 is actually add-on 1.
    const chargeIndex = skipInitialPayment ? index + 1 : index;
    return defer(() => {
      this._trackPaymentChargeSubmitted(selectedPaymentMethod, chargePayload, chargeIndex);
      return this.paymentPlatformService.makePayment(chargePayload).pipe(
        switchMap(paymentResult => {
          // no action required
          if (!paymentResult.needsConfirmation) {
            return of(undefined);
          }

          // 3D Secure Authentication required from user
          const paymentActionRequirement: PaymentActionRequirement = {
            paymentIntentId: paymentResult.paymentId,
            clientSecret: paymentResult.clientSecret,
            needsConfirmation: true,
          };
          return this.store.dispatch(new SetCurrentPaymentActionRequirement(paymentActionRequirement));
        }),
        switchMap(_ => this.store.select(PaymentsState.getCurrentPaymentActionRequirement)),
        ignoreTruthy(),
        take(1),
        tap(_ => {
          const updatedExecutedPaymentBatches = PaymentsState.insertUpdatedExecutedPayment(
            this.store.selectSnapshot(PaymentsState.getExecutedPaymentBatches),
            newExecutedPaymentId,
            { status: LoadableStatus.LOADED, data: chargePayload },
            chargeIndex,
          );

          patchState(updatedExecutedPaymentBatches);
          this._trackPaymentChargeSucceeded(selectedPaymentMethod, chargeAmount, chargeIndex);
        }),
        catchError(error => {
          const details = error.error?.extensions?.details;
          const marshaledError: ChargePaymentError = {
            code: error.status,
            graphqlCode: error.error?.extensions?.code,
            message: error.error?.message,
            status: error.status,
            declineCode: details?.stripeDeclineCode,
            stripeErrorCode: details?.stripeErrorCode,
          };

          const updatedExecutedPaymentBatches = PaymentsState.insertUpdatedExecutedPayment(
            this.store.selectSnapshot(PaymentsState.getExecutedPaymentBatches),
            newExecutedPaymentId,
            { status: LoadableStatus.ERROR, data: chargePayload, error: marshaledError },
            chargeIndex,
          );

          patchState(updatedExecutedPaymentBatches);
          this._trackChargeFailed(selectedPaymentMethod, chargePayload, marshaledError, error, chargeIndex);

          // First non-skipped charge (initial when present, otherwise first add-on) re-throws to stop the concat;
          // later add-on failures are swallowed so subsequent add-ons still get attempted.
          const shouldThrowError = chargeIndex === (skipInitialPayment ? 1 : 0);
          return shouldThrowError ? throwError(error) : of(undefined);
        }),
      );
    });
  }

  private _makePaymentCancellation$(): Observable<unknown> {
    return merge(
      this.store.select(PaymentsState.getCurrentPaymentCancelled).pipe(ignoreFalsy()),
      this.store.select(PaymentsState.isLastExecutedPaymentBatchComplete).pipe(
        skip(1), // skip the initial value (emits before current payment batch begins)
        ignoreFalsy(),
      ),
    );
  }

  @Action([MakePayment])
  makePayment({ patchState }: StateContext<PaymentsStateModel>): Observable<LoadableStatus | PaymentActionRequirement | void> {
    // Mint the batch correlation ID up front so every workflow event during this lifecycle (charges,
    // platform confirmations, cancel, errors) carries the same `paymentBatchId`. We don't bother
    // clearing it explicitly — no events read it after the makePayment observable settles, and the
    // next MakePayment overwrites it with a fresh UUID.
    patchState({ currentPaymentBatchId: uuidv4() });

    return combineLatest([
      this.paymentPlatformService.getSelectedPaymentMethod(),
      this.paymentPlatformService.getAdditionalPaymentData(),
      this.store.selectOnce(PaymentsState.doAppliedCreditsCoverArTranAllocations),
      this.store.select(PaymentsState.getQuickPaymentLinkId),
    ]).pipe(
      tap(([selectedPaymentMethod]) => this._assertMakePaymentPreconditions(selectedPaymentMethod)),
      map(([selectedPaymentMethod, additionalPaymentData, doCreditsCoverArTranAllocations, quickPaymentLinkId]) => ({
        selectedPaymentMethod,
        chargePayloads: this.getChargePayloads(
          selectedPaymentMethod,
          additionalPaymentData,
          this.authAdaptor.isAdmin,
          doCreditsCoverArTranAllocations,
          quickPaymentLinkId,
        ),
      })),
      switchMap(({ selectedPaymentMethod, chargePayloads }) => {
        const newExecutedPaymentId = this._initExecutedPaymentBatch(patchState, chargePayloads);

        // If the first charge payload is undefined, then we're skipping the initial payment (credits cover AR Tran allocations).
        const skipInitialPayment = chargePayloads[0] == undefined;

        return concat(
          skipInitialPayment
            ? this.store.dispatch(new SubmitCreditAllocations(true))
            : of(undefined),

          ...chargePayloads.filter(Boolean).map((chargePayload, index) => this._executeChargePayload(
            patchState,
            chargePayload,
            index,
            selectedPaymentMethod,
            newExecutedPaymentId,
            skipInitialPayment,
          )),
        );
      }),
      takeUntil(this._makePaymentCancellation$()),
      finalize(() => this.store.dispatch(new ClearSuccessfulPaymentData())),
    );
  }

  @Action([SubmitCreditAllocations])
  submitCreditAllocations(
    { patchState, dispatch }: StateContext<PaymentsStateModel>,
    { isAlsoPayingAddOns }: SubmitCreditAllocations,
  ): Observable<unknown> | void {
    const applyCreditsPayload: ApplyCreditsPayload = {
      allocations: this.store.selectSnapshot(PaymentsState.getCreditAllocations),
      notifyToList: this.getAllRecipientEmailsSnapshot(),
    };
    if (isEmpty(applyCreditsPayload.allocations)) {
      this.logger.warn('PAYMENT : PaymentsState : submitCreditAllocations : No credit allocations to apply. Must abort.');
      return;
    }

    let newExecutedPaymentId = this.store.selectSnapshot(PaymentsState.getLastExecutedPaymentBatchId);

    // If we're not paying add-ons, then this is the entire "payment" flow. So we need to create a new executed payment batch.
    if (!isAlsoPayingAddOns) {
      newExecutedPaymentId = (newExecutedPaymentId ?? -1) + 1;
      const existingExecutedPayments = this.store.selectSnapshot(PaymentsState.getExecutedPaymentBatches);
      patchState({
        executedPaymentBatches: {
          ...existingExecutedPayments,
          [newExecutedPaymentId]: { initial: { status: LoadableStatus.LOADING } },
        },
      });
    }

    const totalAmount = Math.abs(
      applyCreditsPayload.allocations.reduce((sum, { allocated }) => sum + allocated, 0),
    );
    this.creditApplicationTrackService.trackWorkflow(
      CreditApplicationWorkflowEvent.CREDIT_APPLICATION_SUBMITTED,
      { totalAmount },
    );
    const creditOperationKey = `CREDIT_APPLICATION_SUBMIT:${newExecutedPaymentId}`;
    this.creditApplicationTrackService.startOperation(creditOperationKey);

    return this.creditApiService.applyCredits(applyCreditsPayload).pipe(
      tap({
        next: _ => {
          const updatedExecutedPaymentBatches = PaymentsState.insertUpdatedExecutedPayment(
            this.store.selectSnapshot(PaymentsState.getExecutedPaymentBatches),
            newExecutedPaymentId,
            { status: LoadableStatus.LOADED, data: applyCreditsPayload },
            0,
          );

          patchState(updatedExecutedPaymentBatches);
          this.creditApplicationTrackService.trackWorkflow(
            CreditApplicationWorkflowEvent.CREDIT_APPLICATION_SUBMIT_SUCCEEDED,
            { totalAmount, operationDurationMs: this.creditApplicationTrackService.endOperation(creditOperationKey) },
          );
        },
        error: (error: ApolloError) => {
          const marshaledError: LoadableError = {
            code: 500,
            message: 'There was an error applying credits.',
          };

          const updatedExecutedPaymentBatches = PaymentsState.insertUpdatedExecutedPayment(
            this.store.selectSnapshot(PaymentsState.getExecutedPaymentBatches),
            newExecutedPaymentId,
            { status: LoadableStatus.ERROR, data: applyCreditsPayload, error: marshaledError },
            0,
          );

          patchState(updatedExecutedPaymentBatches);
          this.creditApplicationTrackService.trackWorkflow(
            CreditApplicationWorkflowEvent.CREDIT_APPLICATION_SUBMIT_FAILED,
            {
              ...extractFailureContext(error),
              totalAmount,
              operationDurationMs: this.creditApplicationTrackService.endOperation(creditOperationKey),
            },
          );
        },
      }),
      switchMap(_ => isAlsoPayingAddOns ? EMPTY : dispatch(new ClearSuccessfulPaymentData())),
    );
  }

  @Action([SetCurrentPaymentActionRequirement])
  setCurrentPaymentActionRequirement(
    { patchState }: StateContext<PaymentsStateModel>,
    { paymentActionRequirement }: SetCurrentPaymentActionRequirement,
  ): void {
    patchState({ currentPaymentActionRequirement: paymentActionRequirement });
  }

  @Action([ClearCurrentPaymentActionRequirement])
  clearCurrentPaymentActionRequirement({ patchState }: StateContext<PaymentsStateModel>): void {
    patchState({ currentPaymentActionRequirement: undefined });
  }

  @Action([CancelCurrentPayment])
  cancelCurrentPayment(
    { patchState }: StateContext<PaymentsStateModel>,
    { cancellationReason, cancellationSource }: CancelCurrentPayment,
  ): Observable<unknown> {
    const cancelEventBase = PaymentsState.buildPaymentEventBase(this.store);
    this.paymentTrackService.trackWorkflow(PaymentWorkflowEvent.PAYMENT_CANCELLED, {
      ...cancelEventBase,
      cancellationReason: cancellationReason?.message ?? 'Unknown cancellation reason',
      cancellationSource,
    });

    const currentPaymentActionRequirement = this.store.selectSnapshot(PaymentsState.getCurrentPaymentActionRequirement);
    const currentExecutedPaymentBatch = this.store.selectSnapshot(PaymentsState.getLastExecutedPaymentBatch);
    const currentExecutedPaymentBatchId = this.store.selectSnapshot(PaymentsState.getLastExecutedPaymentBatchId);

    let updatedExecutedPaymentBatch: ExecutedPaymentBatch = null;
    if (LoadableHelper.isLoading(currentExecutedPaymentBatch.initial.status)) {
      // mark initial payment as failed
      updatedExecutedPaymentBatch = {
        initial: {
          ...currentExecutedPaymentBatch.initial,
          status: LoadableStatus.ERROR,
          error: { code: 0, message: cancellationReason.message },
        },
      };
    } else if ('addOns' in currentExecutedPaymentBatch) {
      // last add-on attempt is marked as failed and all incomplete payments are removed
      const cancelledAddOnIndex = currentExecutedPaymentBatch.addOns.findIndex(({ status }) => !LoadableHelper.isLoading(status)) + 1;
      const addOns = currentExecutedPaymentBatch.addOns.slice(0, cancelledAddOnIndex + 1);
      addOns[addOns.length - 1] = {
        ...addOns[addOns.length - 1],
        status: LoadableStatus.ERROR,
        error: { code: 0, message: cancellationReason.message },
      };

      updatedExecutedPaymentBatch = {
        initial: currentExecutedPaymentBatch.initial,
        addOns,
      };
    }

    patchState({
      executedPaymentBatches: {
        ...this.store.selectSnapshot(PaymentsState.getExecutedPaymentBatches),
        [currentExecutedPaymentBatchId]: updatedExecutedPaymentBatch,
      },
      currentPaymentActionRequirement: {
        ...currentPaymentActionRequirement,
        cancelled: true, // signal to complete this payment's batch's processing (is cleared after cancelPayment() is successful)
      },
    });

    // User-cancellation is already tracked as PAYMENT_CANCELLED at the top of this action; the
    // platform cancelPayment leg's failure is an internal teardown error, not a payment outcome
    // the user can observe. Swallow it from the tracking stream — `PaymentCancellationSource`
    // surfaces the cancel context via the prior PAYMENT_CANCELLED event.
    return this.paymentPlatformService.cancelPayment({
      ppId: currentPaymentActionRequirement.paymentIntentId,
      failureReason: currentPaymentActionRequirement.needsConfirmation ? '3D Secure Authentication Canceled' : 'Miscellaneous',
    }).pipe(
      switchMap(_ => this.store.dispatch(new ClearCurrentPaymentActionRequirement())),
    );
  }

  @Action([ClearSuccessfulPaymentData])
  clearSuccessfulPaymentData(_: StateContext<PaymentsStateModel>): Observable<unknown> | void {
    const actions = this._getClearSuccessfulPaymentDataActions();
    if (actions.length === 0) {
      return;
    }

    return combineLatest(actions);
  }

  private _getClearSuccessfulPaymentDataActions(): Observable<unknown>[] {
    const actions: Observable<unknown>[] = [];

    const lastExecutedPayment = this.store.selectSnapshot(PaymentsState.getLastExecutedPaymentBatch);
    if (lastExecutedPayment == undefined) {
      return [];
    }

    if (lastExecutedPayment.initial.status === LoadableStatus.ERROR) {
      return [];
    }

    const quickPaymentLinkIdStatus = this.store.selectSnapshot(PaymentsState.getQuickPaymentLinkIdStatus);
    if (quickPaymentLinkIdStatus !== LoadableStatus.DEFAULT) {
      actions.push(this.store.dispatch(new ClearQuickPaymentLinkId()));
    }

    const initialPayment = lastExecutedPayment.initial.data;
    const wasInitialChargeAPayment = 'chargeDetails' in initialPayment; // as opposed to a credit application
    const didPayWithoutInvoice = wasInitialChargeAPayment && 'invoiceAddOnTypeId' in initialPayment.chargeDetails.paymentDetails;
    const selectedLateFeesTotal = this.store.selectSnapshot(PaymentsState.getSelectedLateFeesTotal);
    const payLateFeesByClientEnabled = this.store.selectSnapshot(
      LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_APPLY_CREDITS_TO_LATE_FEES),
    );

    if (selectedLateFeesTotal > 0) {
      actions.push(this.store.dispatch([
        new GetLateFees(true),
        ...(payLateFeesByClientEnabled && !didPayWithoutInvoice ? [new ClearPayLateFeeClientIds()] : []),
      ]));
    }

    if (wasInitialChargeAPayment && didPayWithoutInvoice) {
      actions.push(this.store.dispatch(new RemovePayWithoutInvoicePayable()));
      return actions;
    }

    const arTranAllocations = this.store.selectSnapshot(PaymentsState.getArTranAllocations);
    const selectedPayables = this.store.selectSnapshot(PaymentsState.getSelectedPayables);
    const wereAnyArTransPartiallyAllocated = arTranAllocations.some(arTranAllocation => {
      const matchingPayable = selectedPayables.find(({ arTranId }) => arTranId === arTranAllocation.arTranId);
      return matchingPayable.amountRemaining > arTranAllocation.allocatedAmount;
    });

    const wereCreditsApplied = !wasInitialChargeAPayment
      || ('creditAllocations' in initialPayment.chargeDetails && !isEmpty(initialPayment.chargeDetails.creditAllocations));

    actions.push(
      // need to wait for payment to be applied to AR Tran Allocations before refreshing payables
      this.store.dispatch([
        new SetPayWithInvoices(wereAnyArTransPartiallyAllocated),
        new ApplyPaymentToArTranAllocations(),
        ...(wereCreditsApplied ? [new ClearCreditAppliedClientIds(), new GetCredits(true)] : []),
      ]).pipe(
        switchMap(_ => this.store.dispatch(new GetPayables(true))),
      ),
    );

    if (!('addOns' in lastExecutedPayment) || isEmpty(lastExecutedPayment.addOns)) {
      return actions;
    }

    const failedAddOnPayments = lastExecutedPayment.addOns.filter(({ status }) => status === LoadableStatus.ERROR);
    if (failedAddOnPayments.length === 0) {
      actions.push(this.store.dispatch(new ClearAddOns()));
      return actions;
    }

    const addOns = this.store.selectSnapshot(PaymentsState.getAddOns);
    const addOnOptions = this.store.selectSnapshot(PaymentsState.getAddOnOptions);
    const failedAddons = failedAddOnPayments.map(({ data }) => {
      const addOnPaymentDetails = data.chargeDetails.paymentDetails;
      return addOns.find(addOn =>
        addOn.account.id === addOnPaymentDetails.clientId
        && addOn.type === addOnOptions.find(({ id }) => id === addOnPaymentDetails.invoiceAddOnTypeId).type
        && +addOn.paymentAmount === addOnPaymentDetails.amount
        && addOn.notes === addOnPaymentDetails.notes,
      );
    });

    actions.push(this.store.dispatch(new SetAddOns(failedAddons)));
    return actions;
  }

  @Action([ClearLateFees])
  clearLateFees({ patchState }: StateContext<PaymentsStateModel>): void {
    const lateFeesLoadable = this.store.selectSnapshot(PaymentsState.getLateFeesLoadable);
    patchState({ lateFees: { ...lateFeesLoadable, data: [] } });
  }

  @Action([SetAddOns])
  setAddOns({ patchState }: StateContext<PaymentsStateModel>, { addOns }: SetAddOns): void {
    patchState({ addOns });
  }

  @Action([RemovePayWithoutInvoicePayable])
  removePayWithoutInvoicePayable({ patchState }: StateContext<PaymentsStateModel>): void {
    patchState({ payWithoutInvoicePayable: undefined });
  }

  @Action([ClearAddOns])
  clearAddOns({ patchState }: StateContext<PaymentsStateModel>): void {
    patchState({ addOns: undefined });
  }

  /**
   * Helper function to update the corresponding payment within the current batch with the new status (and error message if applicable).
   * @param existingExecutedPaymentBatches - previously executed payment batches and the current payment being executed
   * @param newExecutedPaymentId - 0 based index of payment batches executed during this session
   * @param chargePayloadLoadable - result of a single payment execution
   * @param index - 0 === initial payment, 1+ === add-on payments
   */
  static insertUpdatedExecutedPayment(
    existingExecutedPaymentBatches: ExecutedPaymentBatches,
    newExecutedPaymentId: number,
    chargePayloadLoadable: Loadable<ChargePayload | ApplyCreditsPayload>,
    index: number,
  ): Pick<PaymentsStateModel, 'executedPaymentBatches'> {
    const isInitialPayment = index === 0;
    const currentExecutedPayment = existingExecutedPaymentBatches[newExecutedPaymentId];
    const addOnExecutedPayments = 'addOns' in currentExecutedPayment ? (currentExecutedPayment.addOns ?? []) : [];

    return {
      executedPaymentBatches: {
        ...existingExecutedPaymentBatches,
        [newExecutedPaymentId]: {
          ...(currentExecutedPayment ?? {}),
          ...(
            isInitialPayment
              ? { initial: chargePayloadLoadable }
              : { addOns: replaceArrayItem(addOnExecutedPayments, index - 1, chargePayloadLoadable) }
          ),
        } as ExecutedPaymentBatch,
      },
    };
  }

  @Action([ResetState])
  resetState({ setState }: StateContext<PaymentsStateModel>): Observable<unknown> {
    setState(DEFAULT_PAYMENTS_STATE_MODEL);
    return this.paymentPlatformService.reset().pipe(
      take(1),
    );
  }
}
